<template>
  <div class="page-wrap" :class="{ 'picker-mode': isPickerMode }">
    <!-- 左侧边栏：知识点标签和题型标签 -->
    <div class="sidebar">
      <!-- 标签筛选 -->
      <div class="sidebar-section">
        <div class="sidebar-title">
          <el-radio-group v-model="activeTagTab" size="small">
            <el-radio-button label="knowledge">章节标签</el-radio-button>
            <el-radio-button label="question">题型标签</el-radio-button>
          </el-radio-group>
        </div>
        <div class="sidebar-tree" v-loading="tagTreeLoading">
          <el-tree
            v-show="activeTagTab === 'knowledge'"
            ref="knowledgeTree"
            :data="knowledgeTreeData"
            :props="{ label: 'name', children: 'children' }"
            node-key="id"
            highlight-current
            :expand-on-click-node="false"
            :default-expanded-keys="knowledgeExpandedKeys"
            @node-click="onKnowledgeNodeClick"
          >
            <span slot-scope="{ node, data }" class="tree-node">
              <span class="tree-label">{{ node.label }}</span>
            </span>
          </el-tree>
          <el-tree
            v-show="activeTagTab === 'question'"
            ref="questionTree"
            :data="questionTagTreeData"
            :props="{ label: 'name', children: 'children' }"
            node-key="id"
            highlight-current
            :expand-on-click-node="false"
            :default-expanded-keys="questionTagExpandedKeys"
            @node-click="onQuestionTagNodeClick"
          >
            <span slot-scope="{ node, data }" class="tree-node">
              <span class="tree-label">{{ node.label }}</span>
            </span>
          </el-tree>
        </div>
      </div>
    </div>

    <!-- 右侧主内容区 -->
    <div class="main-content">
      <!-- 筛选 + 操作区 -->
      <el-card class="filter-card" shadow="never">
        <!-- 顶部筛选：学科 + 学段 + 搜索 -->
        <div class="top-bar">
          <div style="display:flex;align-items:center;gap:12px">
            <el-select v-if="!isTeacherMode" v-model="selectedSubjectId" placeholder="请选择学科" style="width:140px" @change="selectSubject" clearable>
              <el-option v-for="s in subjects" :key="s.id" :label="s.name" :value="s.id" />
            </el-select>
            <el-tag v-else-if="teacherMe && teacherMe.subjectName" type="info" style="margin-right:8px">学科：{{ teacherMe.subjectName }}</el-tag>
            <el-select v-model="selectedStageId" placeholder="请选择学段" style="width:140px" @change="selectStage" clearable :disabled="!selectedSubjectId">
              <el-option v-for="s in stages" :key="s.id" :label="s.name" :value="s.id" />
            </el-select>
            <el-input
              v-model="filterForm.keyword"
              placeholder="搜索题目内容关键词..."
              prefix-icon="el-icon-search"
              clearable
              style="width:260px"
              @keyup.enter.native="onFilterChange"
              @clear="onFilterChange"
            />
          </div>
          <div class="top-bar-right" v-if="!isPickerMode">
            <el-button type="primary" icon="el-icon-plus" @click="openAddDialog">新增题目</el-button>
            <el-button icon="el-icon-document" @click="downloadTemplate">下载模版</el-button>
            <el-upload
              :action="importUrl"
              :headers="uploadHeaders"
              :show-file-list="false"
              accept=".xlsx,.xls"
              :on-success="onImportSuccess"
              :on-error="onImportError"
              style="display:inline-block;margin-left:8px"
            >
              <el-button icon="el-icon-upload2">Excel 导入</el-button>
            </el-upload>
          </div>
        </div>

        <el-divider style="margin:12px 0" />

        <el-alert
          v-if="!isPickerMode"
          title="导入说明：当前仅支持单选/多选；支持题目内容/选项/解析插图；单张图片建议不超过2MB，每格最多3张。"
          type="info"
          :closable="false"
          show-icon
          style="margin-bottom:12px"
        />

        <el-card v-if="!isPickerMode && importTask.running" shadow="never" style="margin-bottom:12px;background:#f8fbff;border-color:#dbe8ff;">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
            <span style="font-size:13px;color:#2f4a78;">导入任务进行中：{{ importTask.filename || 'question_template.xlsx' }}</span>
            <span style="font-size:12px;color:#6b7ea3;">{{ importTask.progress }}%</span>
          </div>
          <el-progress :percentage="importTask.progress" :stroke-width="12" :text-inside="true" />
          <div style="margin-top:8px;font-size:12px;color:#6b7ea3;">
            已处理 {{ importTask.processed }} / {{ importTask.total || '--' }} 行，成功 {{ importTask.successCount }}，跳过 {{ importTask.skipCount }}，失败 {{ importTask.errorCount }}
          </div>
        </el-card>

        <!-- 行内点击筛选 -->
        <div class="filter-rows">
          <div class="filter-row">
            <span class="filter-label">年级</span>
            <div class="filter-opts">
              <span
                class="filter-opt"
                :class="{ active: filterForm.gradeId === null }"
                @click="onGradeChange(null)"
              >全部</span>
              <span
                v-for="g in filteredGrades"
                :key="g.id"
                class="filter-opt"
                :class="{ active: filterForm.gradeId === g.id }"
                @click="onGradeChange(g.id)"
              >{{ g.name }}</span>
            </div>
          </div>

          <div class="filter-row">
            <span class="filter-label">章节</span>
            <div class="filter-opts">
              <span class="filter-opt" :class="{ active: filterForm.chapterId === null }" @click="setFilter('chapterId', null)">全部</span>
              <span
                v-for="c in subjectChapters"
                :key="c.id"
                class="filter-opt"
                :class="{ active: filterForm.chapterId === c.id }"
                @click="setFilter('chapterId', c.id)"
              >{{ c.displayName || c.name }}</span>
            </div>
          </div>

          <div class="filter-row">
            <span class="filter-label">状态</span>
            <div class="filter-opts">
              <span class="filter-opt" :class="{ active: filterForm.status === null }" @click="setFilter('status', null)">全部</span>
              <span class="filter-opt" :class="{ active: filterForm.status === 1 }" @click="setFilter('status', 1)">已上架</span>
              <span class="filter-opt" :class="{ active: filterForm.status === 0 }" @click="setFilter('status', 0)">已下架</span>
            </div>
          </div>

          <div class="filter-row">
            <span class="filter-label">标签</span>
            <div class="filter-opts">
              <span class="filter-opt" :class="{ active: filterForm.tagFilter === null }" @click="setTagFilter(null)">全部</span>
              <span class="filter-opt" :class="{ active: filterForm.tagFilter === 'untagged' }" @click="setTagFilter('untagged')">未加标签</span>
            </div>
          </div>

          <div class="filter-row">
            <span class="filter-label">学期</span>
            <div class="filter-opts">
              <span class="filter-opt" :class="{ active: filterForm.semester === null }" @click="setFilter('semester', null)">全部</span>
              <span v-for="s in semesters" :key="s.name"
                class="filter-opt" :class="{ active: filterForm.semester === s.name }" @click="setFilter('semester', s.name)">{{ s.name }}</span>
            </div>
          </div>

          <div class="filter-row">
            <span class="filter-label">考试类型</span>
            <div class="filter-opts">
              <span class="filter-opt" :class="{ active: filterForm.examType === null }" @click="setFilter('examType', null)">全部</span>
              <span v-for="e in examTypes" :key="e.name"
                class="filter-opt" :class="{ active: filterForm.examType === e.name }" @click="setFilter('examType', e.name)">{{ e.name }}</span>
            </div>
          </div>

          <div class="filter-row">
            <span class="filter-label">地区</span>
            <div class="filter-opts">
              <span class="filter-opt" :class="{ active: filterForm.region === null }" @click="setFilter('region', null)">全部</span>
              <span v-for="r in regions" :key="r.name"
                class="filter-opt" :class="{ active: filterForm.region === r.name }" @click="setFilter('region', r.name)">{{ r.name }}</span>
            </div>
          </div>

          <div class="filter-row">
            <span class="filter-label">学年</span>
            <div class="filter-opts">
              <span class="filter-opt" :class="{ active: filterForm.schoolYear === null }" @click="setFilter('schoolYear', null)">全部</span>
              <span v-for="y in schoolYears" :key="y.name"
                class="filter-opt" :class="{ active: filterForm.schoolYear === y.name }" @click="setFilter('schoolYear', y.name)">{{ y.name }}</span>
            </div>
          </div>
        </div>
      </el-card>

    <!-- 题目数量 -->
    <div class="list-meta">
      <el-checkbox
        :indeterminate="batchSelectedIds.length > 0 && batchSelectedIds.length < tableData.length"
        :value="tableData.length > 0 && tableData.every(r => batchSelectedIds.includes(r.id))"
        @change="toggleSelectAllPage"
      >全选当前页</el-checkbox>
      <el-button
        v-if="!isPickerMode && deletableBatchCount > 0"
        type="danger"
        size="small"
        plain
        style="margin-left:16px"
        @click="batchDelete"
      >批量删除 ({{ deletableBatchCount }})</el-button>
      <span style="margin-left:16px;color:#666;font-size:13px">共 <strong>{{ total }}</strong> 道题目</span>
      <el-button type="text" @click="resetFilter" style="margin-left:12px;color:#999">
        <i class="el-icon-refresh-left"></i> 重置筛选
      </el-button>
    </div>

    <!-- 题目卡片列表 -->
    <div v-loading="loading" class="question-list">
      <!-- 空状态 -->
      <el-empty v-if="!loading && tableData.length === 0" :description="isPickerMode ? '暂无符合条件的题目' : '暂无题目，点击新增开始添加'" />

      <div v-for="(row, index) in tableData" :key="row.id"
        class="q-card"
        :class="{
          'q-card-selected': batchSelectedIds.includes(row.id),
          'q-card-added': isPickerMode && isExistingHandoutQuestion(row.id)
        }"
      >
        <!-- 题目头部 -->
        <div class="q-header">
          <el-checkbox
            :value="batchSelectedIds.includes(row.id)"
            :disabled="isPickerMode && isExistingHandoutQuestion(row.id)"
            @change="toggleBatchSelect(row.id)"
            @click.native.stop
            style="margin-right:6px;flex-shrink:0"
          />
          <span class="q-num">{{ page * size + index + 1 }}</span>
          <el-tag :type="typeTagMap[row.type]" size="small">{{ typeNameMap[row.type] }}</el-tag>
          <el-tag v-if="getFillSubSummary(row)" size="mini" type="info" style="margin-left:4px">
            {{ getFillSubSummary(row).count }}小问 · {{ getFillSubSummary(row).totalScore }}分
          </el-tag>
          <div class="q-difficulty" @click.stop>
            <el-rate
              :value="normalizeDifficulty(row.difficulty)"
              :max="5"
              :colors="['#F7BA2A', '#F7BA2A', '#F7BA2A']"
              void-color="#dcdfe6"
              :disabled="!canTeacherEditQuestion(row)"
              @change="val => updateDifficulty(row, val)"
            />
          </div>
          <div class="q-meta-tags">
            <span v-if="row.grade" class="meta-chip">{{ row.grade.name }}</span>
            <span v-if="row.subject" class="meta-chip">{{ row.subject.name }}</span>
            <span v-if="row.paper" class="meta-chip paper">{{ row.paper.name }}</span>
            <span v-if="row.chapter" class="meta-chip">{{ chapterDisplayName(row.chapter) }}</span>
            <span v-if="row.source" class="meta-chip source">{{ row.source }}</span>
          </div>
          <el-tag v-if="isPickerMode && isExistingHandoutQuestion(row.id)" size="mini" type="success" style="margin-left:8px">已在讲义</el-tag>
          <el-tag v-else-if="isTeacherMode && !canTeacherEditQuestion(row)" size="mini" type="info" style="margin-left:8px">管理员录入</el-tag>
          <el-tag v-else :type="row.status === 1 ? 'success' : 'info'" size="mini" style="margin-left:auto">
            {{ row.status === 1 ? '上架' : '下架' }}
          </el-tag>
        </div>

        <!-- 题目内容 -->
        <div
          class="q-content rich-html"
          :class="{
            'has-table': hasTableContent(row.content),
            'has-inline-images': hasInlineImages(row.content),
            'has-block-diagram': hasBlockDiagramInHtml(row.content),
            'fill-sub-preview': row.type === 'FILL' && getFillSubSummary(row)
          }"
          v-html="renderHtml(getQuestionPreviewHtml(row))"
        ></div>

        <!-- 选项展示 -->
        <div class="q-options" v-if="parseOptions(row.optionsJson).length">
          <div v-for="(opt, i) in parseOptions(row.optionsJson)" :key="i" class="q-option-item">
            <span class="q-option-key">{{ opt.key }}.</span>
            <div class="q-option-value rich-html" v-html="renderOptionHtml(opt.value)"></div>
          </div>
        </div>

        <!-- 标签展示区 -->
        <div class="q-tags-row">
          <!-- 知识点标签（只展示三级叶子名） -->
          <template v-if="row.knowledgeTags">
            <i class="el-icon-price-tag q-tags-icon"></i>
            <el-tag
              v-for="tag in getLeafTagNames(row.knowledgeTags)"
              :key="tag"
              size="mini"
              type="info"
              class="q-tag-item"
            >{{ tag }}</el-tag>
          </template>
          <!-- 题型标签（只展示三级叶子名） -->
          <template v-if="row.questionTag">
            <i v-if="row.knowledgeTags" class="q-tags-sep">|</i>
            <el-tag
              v-for="tag in getLeafTagNames(row.questionTag)"
              :key="'q'+tag"
              size="mini"
              type="warning"
              class="q-tag-item"
            >{{ tag }}</el-tag>
          </template>
          <!-- 快捷打标签按钮 -->
          <el-button
            v-if="!isPickerMode && canTeacherEditQuestion(row)"
            type="text"
            size="mini"
            icon="el-icon-price-tag"
            class="q-tag-btn"
            @click.stop="openTagQuickDialog(row)"
          >{{ (row.knowledgeTags || row.questionTag) ? '改标签' : '+打标签' }}</el-button>
        </div>

        <!-- 底部操作 -->
        <div v-if="!isPickerMode" class="q-footer">
          <span class="q-time">{{ formatTime(row.updatedAt) }}</span>
          <div class="q-actions">
            <el-button type="text" icon="el-icon-circle-plus-outline" style="color:#1677ff" @click="quickAddToPaper(row)">加入试卷</el-button>
            <template v-if="canTeacherEditQuestion(row)">
              <el-divider direction="vertical" />
              <el-button type="text" icon="el-icon-edit" @click="openEditDialog(row)">编辑</el-button>
              <el-divider direction="vertical" />
              <el-button
                type="text"
                :icon="row.status === 1 ? 'el-icon-download' : 'el-icon-upload'"
                @click="toggleStatus(row)"
              >{{ row.status === 1 ? '下架' : '上架' }}</el-button>
              <el-divider direction="vertical" />
              <el-button type="text" icon="el-icon-delete" class="danger-text" @click="deleteQuestion(row)">删除</el-button>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- 分页 -->
    <el-pagination
      class="pagination"
      @size-change="handleSizeChange"
      @current-change="handlePageChange"
      :current-page="page + 1"
      :page-sizes="[10, 20, 50]"
      :page-size="size"
      :total="total"
      layout="total, sizes, prev, pager, next"
    />

    <!-- 批量操作悬浮栏 -->
    <transition name="slide-up">
      <div v-if="!isPickerMode && batchSelectedIds.length > 0" class="batch-bar">
        <span class="batch-info">已勾选 <strong>{{ batchSelectedIds.length }}</strong> 道题目</span>
        <el-button size="small" @click="batchSelectedIds = []">取消勾选</el-button>
        <el-button size="small" type="primary" icon="el-icon-document-copy" @click="openBatchAddDialog">批量加入试卷</el-button>
      </div>
      <div v-else-if="isPickerMode && batchSelectedIds.length > 0" class="batch-bar picker-batch-bar">
        <span class="batch-info">已勾选 <strong>{{ pickerSelectedCount }}</strong> 道可加入题目</span>
        <el-button size="small" @click="batchSelectedIds = []">取消勾选</el-button>
        <el-button size="small" type="primary" :disabled="!pickerSelectedCount" :loading="handoutBasketLoading" @click="addSelectedToHandout(false)">加入试题篮</el-button>
        <el-button size="small" type="primary" plain :disabled="!pickerSelectedCount" :loading="handoutBasketLoading" @click="addSelectedToHandout(true)">加入并插入正文</el-button>
      </div>
    </transition>

    <!-- 新增/编辑 Dialog -->
    <el-dialog
      :title="editMode ? '编辑题目' : '新增题目'"
      :visible.sync="dialogVisible"
      width="720px"
      destroy-on-close
      @opened="onQuestionDialogOpened"
      @closed="resetForm"
    >
      <el-form :model="editForm" :rules="editRules" ref="editForm" label-width="90px">
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="年级" prop="gradeId">
              <el-select v-model="editForm.gradeId" placeholder="请选择" style="width:100%" @change="onFormGradeChange">
                <el-option v-for="g in grades" :key="g.id" :label="g.name" :value="Number(g.id)" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12" v-if="!isTeacherMode">
            <el-form-item label="学科" prop="subjectId">
              <el-select v-model="editForm.subjectId" placeholder="请选择" style="width:100%" @change="onFormSubjectChange">
                <el-option v-for="s in subjects" :key="s.id" :label="s.name" :value="Number(s.id)" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12" v-else>
            <el-form-item label="学科">
              <el-input :value="teacherMe && teacherMe.subjectName ? teacherMe.subjectName : '本学科'" disabled />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="题型" prop="type">
              <el-radio-group v-model="editForm.type" @change="onQuestionTypeChange">
                <el-radio label="SINGLE">单选</el-radio>
                <el-radio label="MULTI">多选</el-radio>
                <el-radio label="FILL">填空</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row v-if="editForm.type === 'FILL'" :gutter="16">
          <el-col :span="24">
            <div class="fill-sub-banner">
              <span>多小问填空题：先填共用材料，再添加 (1)(2) 子题并分别设分</span>
              <el-button type="primary" size="small" icon="el-icon-plus" @click="addSubQuestion">添加子题</el-button>
              <span v-if="isFillSubMode" class="sub-total-score">共 {{ subQuestionTotalScore }} 分</span>
            </div>
          </el-col>
        </el-row>
        <el-row :gutter="16">
          <el-col :span="24">
            <el-form-item label="加入试卷">
              <el-select
                v-model="editForm.addToPaperId"
                placeholder="可选，保存后自动加入所选试卷"
                clearable
                filterable
                style="width:100%"
                :disabled="!editForm.gradeId || (!editForm.subjectId && !isTeacherMode)"
              >
                <el-option
                  v-for="p in formPapers"
                  :key="p.id"
                  :label="paperOptionLabel(p)"
                  :value="Number(p.id)"
                />
              </el-select>
              <div class="form-tip">需先选择年级和学科；试卷来自「试卷管理」，选填</div>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="学期">
              <el-select v-model="editForm.semester" placeholder="可选" clearable style="width:100%" @change="generateQuestionSource">
                <el-option v-for="s in semesters" :key="s.name" :label="s.name" :value="s.name" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="考试类型">
              <el-select v-model="editForm.examType" placeholder="可选" clearable filterable allow-create style="width:100%" @change="generateQuestionSource">
                <el-option v-for="e in examTypes" :key="e.name" :label="e.name" :value="e.name" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="学年">
              <el-select v-model="editForm.schoolYear" placeholder="可选" clearable filterable allow-create style="width:100%" @change="generateQuestionSource">
                <el-option v-for="y in schoolYears" :key="y.name" :label="y.name" :value="y.name" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="难度">
              <el-rate
                v-model="editForm.difficulty"
                :max="5"
                :colors="['#F7BA2A', '#F7BA2A', '#F7BA2A']"
                void-color="#dcdfe6"
                @change="onEditDifficultyChange"
              />
              <span class="form-tip" style="margin-left:8px">2～5 星，默认 2 星</span>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="地区">
          <div class="region-cascade">
            <el-select v-model="regionPicker.l1" placeholder="省/全国" clearable filterable style="width:160px" @change="onRegionL1Change">
              <el-option v-for="r in regionTree" :key="r.id" :label="r.name" :value="r.id" />
            </el-select>
            <el-select v-model="regionPicker.l2" placeholder="城市或城区" clearable filterable style="width:160px" :disabled="!regionPicker.l1" @change="onRegionL2Change">
              <el-option v-for="c in regionL2Options" :key="c.id" :label="c.name" :value="c.id" />
            </el-select>
            <el-select v-model="regionPicker.l3" placeholder="学校" clearable filterable style="width:180px" :disabled="!regionPicker.l2" @change="onRegionL3Change">
              <el-option v-for="s in regionL3Options" :key="s.id" :label="s.name" :value="s.id" />
            </el-select>
          </div>
          <div class="form-tip">在「基础数据管理 → 地区」中维护三级结构；后两级可选</div>
        </el-form-item>
        <el-form-item label="题目来源">
          <el-input v-model="editForm.source" placeholder="根据上方字段自动生成，也可手动修改" clearable maxlength="200" show-word-limit />
          <div class="form-tip">格式示例：2023-2024北京海淀101中学高一（上）期中化学；学校可不选（如区统考）</div>
        </el-form-item>
        <!-- 章节标签三级联动 -->
        <el-form-item label="章节标签">
          <div class="tag-selector-wrap">
            <div class="tag-cascade-selectors">
              <el-select v-model="tagPicker.kL1" placeholder="书标签" clearable size="small" style="width:120px" @change="onTagPickerKL1Change">
                <el-option v-for="n in tagPickerKL1" :key="n.id" :label="n.name" :value="n.id" />
              </el-select>
              <el-select v-model="tagPicker.kL2" placeholder="章标签" clearable size="small" style="width:120px" :disabled="!tagPicker.kL1" @change="onTagPickerKL2Change">
                <el-option v-for="n in tagPickerKL2" :key="n.id" :label="n.name" :value="n.id" />
              </el-select>
              <el-select v-model="tagPicker.kL3" placeholder="节标签" clearable size="small" style="width:120px" :disabled="!tagPicker.kL2">
                <el-option v-for="n in tagPickerKL3" :key="n.id" :label="n.name" :value="n.id" />
              </el-select>
              <el-button size="small" type="primary" plain icon="el-icon-plus" @click="addKnowledgeTag" :disabled="!tagPicker.kL3">添加</el-button>
            </div>
            <div v-if="editForm.knowledgeTagItems.length > 0" class="tag-selected-list">
              <el-tag
                v-for="(item, idx) in editForm.knowledgeTagItems"
                :key="item.tagId"
                closable
                size="small"
                type="primary"
                @close="removeKnowledgeTag(idx)"
                style="margin:2px 4px 2px 0"
              >{{ item.pathName }}</el-tag>
            </div>
            <div v-else class="form-tip">请依次选择书→章→节标签后点添加</div>
          </div>
        </el-form-item>
        <!-- 题型标签三级联动 -->
        <el-form-item label="题型标签">
          <div class="tag-selector-wrap">
            <div class="tag-cascade-selectors">
              <el-select v-model="tagPicker.qL1" placeholder="一级" clearable size="small" style="width:120px" @change="onTagPickerQL1Change">
                <el-option v-for="n in tagPickerQL1" :key="n.id" :label="n.name" :value="n.id" />
              </el-select>
              <el-select v-model="tagPicker.qL2" placeholder="二级" clearable size="small" style="width:120px" :disabled="!tagPicker.qL1" @change="onTagPickerQL2Change">
                <el-option v-for="n in tagPickerQL2" :key="n.id" :label="n.name" :value="n.id" />
              </el-select>
              <el-select v-model="tagPicker.qL3" placeholder="三级" clearable size="small" style="width:120px" :disabled="!tagPicker.qL2">
                <el-option v-for="n in tagPickerQL3" :key="n.id" :label="n.name" :value="n.id" />
              </el-select>
              <el-button size="small" type="primary" plain icon="el-icon-plus" @click="addQuestionTag" :disabled="!tagPicker.qL3">添加</el-button>
            </div>
            <div v-if="editForm.questionTagItems.length > 0" class="tag-selected-list">
              <el-tag
                v-for="(item, idx) in editForm.questionTagItems"
                :key="item.tagId"
                closable
                size="small"
                type="warning"
                @close="removeQuestionTag(idx)"
                style="margin:2px 4px 2px 0"
              >{{ item.pathName }}</el-tag>
            </div>
            <div v-else class="form-tip">请依次选择一级→二级→三级标签后点添加</div>
          </div>
        </el-form-item>
        <el-form-item :label="fillContentLabel" prop="content">
          <RichEditor
            v-if="dialogVisible"
            ref="contentEditor"
            :key="'content-' + (editForm.id || 'new') + '-' + editForm.type"
            v-model="editForm.content"
            :placeholder="contentPlaceholder"
            :height="isFillSubMode ? 180 : 220"
            :upload-server="uploadServer"
          />
          <div v-if="editForm.type === 'FILL'" class="form-tip">{{ fillContentTip }}</div>
        </el-form-item>

        <template v-if="editForm.type === 'FILL'">
          <el-form-item label="子题">
            <div class="sub-question-toolbar">
              <el-button type="primary" plain size="small" icon="el-icon-plus" @click="addSubQuestion">添加子题</el-button>
              <span v-if="isFillSubMode" class="sub-total-score">共 {{ subQuestionTotalScore }} 分</span>
              <span v-else class="form-tip" style="margin-left:8px">多小问填空题请点「添加子题」，可按 (1)(2) 分别设分值</span>
            </div>
            <div v-if="isFillSubMode" class="form-tip">小问内容里请用 _____ 或 （    ） 标出填空位置，例如：1+1=_____</div>
          </el-form-item>
          <div v-for="(sub, idx) in editForm.subQuestions" :key="sub.id" class="sub-question-card">
            <div class="sub-question-header">
              <span class="sub-label">{{ sub.label }}</span>
              <span class="sub-score-label">分值</span>
              <el-input-number v-model="sub.score" :min="0" :max="100" :step="0.5" :precision="1" size="small" controls-position="right" />
              <el-button type="text" class="danger-text" @click="removeSubQuestion(idx)">删除</el-button>
            </div>
            <RichEditor
              v-if="dialogVisible"
              ref="subContentEditors"
              :key="'sub-' + (editForm.id || 'new') + '-' + sub.id"
              v-model="sub.content"
              :placeholder="'填写 ' + sub.label + ' 小问内容，空位用 _____ 或 （    ）'"
              :height="160"
              :upload-server="uploadServer"
            />
            <el-input
              v-model="sub.answer"
              size="small"
              placeholder="本小问答案，多空用 | 分隔"
              style="margin-top:8px"
            />
          </div>
        </template>

        <el-form-item v-if="isChoiceQuestion" label="选项">
          <div v-for="(opt, i) in editForm.options" :key="i" class="option-row">
            <span class="option-key">{{ String.fromCharCode(65 + i) }}.</span>
            <RichEditor
              v-if="dialogVisible"
              ref="optionEditors"
              :key="'opt-' + (editForm.id || 'new') + '-' + i"
              v-model="opt.value"
              :placeholder="'选项 ' + String.fromCharCode(65 + i)"
              class="option-editor"
              :upload-server="uploadServer"
            />
            <el-button type="text" class="danger-text" @click="removeOption(i)" v-if="editForm.options.length > 2">✕</el-button>
          </div>
          <el-button type="text" @click="addOption" v-if="editForm.options.length < 6">+ 添加选项</el-button>
        </el-form-item>
        <el-form-item v-if="!isFillSubMode" label="正确答案" prop="answer">
          <el-input v-model="editForm.answer" :placeholder="answerPlaceholder" />
          <div v-if="editForm.type === 'FILL'" class="form-tip">一题多空时，按顺序用 | 分隔，如：氯化钠|离子化合物</div>
        </el-form-item>
        <el-form-item v-else label="合并答案">
          <el-input :value="mergedFillAnswerPreview" readonly />
          <div class="form-tip">由各小问答案按顺序自动合并，导出/组卷时使用</div>
        </el-form-item>
        <el-form-item label="解析">
          <RichEditor
            v-if="dialogVisible"
            ref="analysisEditor"
            :key="'analysis-' + (editForm.id || 'new')"
            v-model="editForm.analysis"
            placeholder="可选，填写解析说明，支持图片、公式等"
            :height="220"
            :upload-server="uploadServer"
          />
        </el-form-item>
        <el-form-item label="状态">
          <el-switch v-model="editForm.status" :active-value="1" :inactive-value="0" active-text="上架" inactive-text="下架" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button v-if="!editMode" :loading="saving" @click="saveAndContinueQuestion">保存并继续新增</el-button>
        <el-button type="primary" :loading="saving" @click="saveQuestion">保存</el-button>
      </template>
    </el-dialog>
    <!-- 组卷对话框：选试卷 + 选题 + 加入 -->
    <el-dialog title="组卷" :visible.sync="composePaperVisible" width="720px" top="5vh" @open="onComposePaperOpen">
      <!-- Step1: 选试卷 -->
      <div class="compose-header">
        <div class="compose-select-paper">
          <span class="compose-label">目标试卷：</span>
          <el-select v-model="targetPaperId" placeholder="请选择试卷（在基础数据-试卷中管理）" filterable style="width:320px">
            <el-option v-for="p in paperOptions" :key="p.id" :label="p.name" :value="p.id" />
          </el-select>
          <span class="compose-tip">{{ isTeacherMode ? '*请选择本学科试卷' : '*试卷名称在《基础数据管理》中维护' }}</span>
        </div>
        <div class="compose-stat">
          已选 <strong style="color:#1677ff">{{ composeSelectedIds.length }}</strong> 题
          <el-button type="text" @click="composeSelectedIds = []" style="margin-left:4px;color:#999">清空</el-button>
        </div>
      </div>

      <!-- 当前题库列表（建议先用左侧筛选条件筛选好才组卷） -->
      <div class="compose-tip-bar">
        <i class="el-icon-info"></i> 展示的是题库当前筛选结果，关闭对话框可继续使用左侧筛选条件筛选
      </div>
      <div v-loading="composLoading" class="compose-list">
        <el-empty v-if="!composLoading && composeQuestions.length === 0" description="暂无题目" />
        <div
          v-for="q in composeQuestions" :key="q.id"
          class="compose-q-row"
          :class="{ 'compose-selected': composeSelectedIds.includes(q.id) }"
          @click="toggleComposeSelect(q.id)"
        >
          <el-checkbox :value="composeSelectedIds.includes(q.id)" @click.native.stop @change="toggleComposeSelect(q.id)" />
          <div class="compose-q-body">
            <div class="compose-q-text">{{ q.content }}</div>
            <div class="compose-q-tags">
              <el-tag size="mini">{{ typeNameMap[q.type] || q.type }}</el-tag>
              <el-tag v-if="q.questionTag" size="mini" type="info">{{ q.questionTag }}</el-tag>
              <span v-if="q.knowledgeTags" style="font-size:11px;color:#bbb;margin-left:4px">{{ q.knowledgeTags }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 分页 + 全选 -->
      <div class="compose-footer">
        <div>
          <el-checkbox
            :indeterminate="composeSelectedIds.length > 0 && composeSelectedIds.length < composeQuestions.length"
            :value="composeQuestions.length > 0 && composeSelectedIds.length === composeQuestions.length"
            @change="toggleSelectAll"
          >全选当前页</el-checkbox>
          <span style="font-size:12px;color:#999;margin-left:8px">共{{ composeTotal }}道题目</span>
        </div>
        <el-pagination
          small
          :current-page="composePage + 1"
          :page-size="composeSize"
          :total="composeTotal"
          layout="prev, pager, next"
          @current-change="onComposePageChange"
        />
      </div>

      <template #footer>
        <el-button @click="composePaperVisible = false">取消</el-button>
        <el-button
          type="primary"
          :disabled="!targetPaperId || composeSelectedIds.length === 0"
          :loading="addToPaperLoading"
          @click="confirmComposePaper"
        >加入试卷（{{ composeSelectedIds.length }}题）</el-button>
      </template>
    </el-dialog>

    <!-- 快捷打标签 Dialog -->
    <el-dialog title="打标签" :visible.sync="tagQuickVisible" width="500px" @closed="resetTagQuickDialog">
      <div class="tag-quick-wrap">
        <!-- 章节标签 -->
        <div class="tag-quick-section">
          <div class="tag-quick-label">章节标签</div>
          <div class="tag-quick-selectors">
            <el-select v-model="tagQuick.kL1" placeholder="书标签" clearable size="small" style="width:110px" @change="onQkKL1Change">
              <el-option v-for="n in tagPickerKL1" :key="n.id" :label="n.name" :value="n.id" />
            </el-select>
            <el-select v-model="tagQuick.kL2" placeholder="章标签" clearable size="small" style="width:110px" :disabled="!tagQuick.kL1" @change="onQkKL2Change">
              <el-option v-for="n in tagQuickKL2" :key="n.id" :label="n.name" :value="n.id" />
            </el-select>
            <el-select v-model="tagQuick.kL3" placeholder="节标签" clearable size="small" style="width:110px" :disabled="!tagQuick.kL2">
              <el-option v-for="n in tagQuickKL3" :key="n.id" :label="n.name" :value="n.id" />
            </el-select>
            <el-button size="small" type="primary" plain icon="el-icon-plus" @click="addTagQuickK" :disabled="!tagQuick.kL3">添加</el-button>
          </div>
          <div class="tag-quick-selected">
            <el-tag v-for="(item,idx) in tagQuickKItems" :key="item.tagId" closable size="small" type="primary" @close="tagQuickKItems.splice(idx,1)" style="margin:2px 4px 2px 0">{{ item.pathName }}</el-tag>
            <span v-if="tagQuickKItems.length === 0" class="tag-quick-empty">未选择</span>
          </div>
        </div>
        <el-divider style="margin:12px 0" />
        <!-- 题型标签 -->
        <div class="tag-quick-section">
          <div class="tag-quick-label">题型标签</div>
          <div class="tag-quick-selectors">
            <el-select v-model="tagQuick.qL1" placeholder="一级" clearable size="small" style="width:110px" @change="onQkQL1Change">
              <el-option v-for="n in tagPickerQL1" :key="n.id" :label="n.name" :value="n.id" />
            </el-select>
            <el-select v-model="tagQuick.qL2" placeholder="二级" clearable size="small" style="width:110px" :disabled="!tagQuick.qL1" @change="onQkQL2Change">
              <el-option v-for="n in tagQuickQL2" :key="n.id" :label="n.name" :value="n.id" />
            </el-select>
            <el-select v-model="tagQuick.qL3" placeholder="三级" clearable size="small" style="width:110px" :disabled="!tagQuick.qL2">
              <el-option v-for="n in tagQuickQL3" :key="n.id" :label="n.name" :value="n.id" />
            </el-select>
            <el-button size="small" type="primary" plain icon="el-icon-plus" @click="addTagQuickQ" :disabled="!tagQuick.qL3">添加</el-button>
          </div>
          <div class="tag-quick-selected">
            <el-tag v-for="(item,idx) in tagQuickQItems" :key="item.tagId" closable size="small" type="warning" @close="tagQuickQItems.splice(idx,1)" style="margin:2px 4px 2px 0">{{ item.pathName }}</el-tag>
            <span v-if="tagQuickQItems.length === 0" class="tag-quick-empty">未选择</span>
          </div>
        </div>
      </div>
      <template #footer>
        <el-button @click="tagQuickVisible = false">取消</el-button>
        <el-button type="primary" :loading="tagQuickSaving" @click="saveTagQuick">保存标签</el-button>
      </template>
    </el-dialog>

    <!-- 单题快捷加入试卷 Dialog -->
    <el-dialog title="加入试卷" :visible.sync="quickAddVisible" width="400px">
      <div style="margin-bottom:12px;font-size:13px;color:#666">选择要加入的试卷：</div>
      <el-select v-model="targetPaperId" placeholder="请选择试卷" style="width:100%" filterable>
        <el-option v-for="p in paperOptions" :key="p.id" :label="p.name" :value="p.id" />
      </el-select>
      <template #footer>
        <el-button @click="quickAddVisible = false">取消</el-button>
        <el-button type="primary" :disabled="!targetPaperId" :loading="addToPaperLoading" @click="confirmQuickAdd">确定加入</el-button>
      </template>
    </el-dialog>

    <!-- 批量加入试卷 Dialog -->
    <el-dialog title="批量加入试卷" :visible.sync="batchAddVisible" width="400px">
      <div style="margin-bottom:12px;font-size:13px;color:#666">将已勾选的 <strong style="color:#1677ff">{{ batchSelectedIds.length }}</strong> 道题加入试卷：</div>
      <el-select v-model="targetPaperId" placeholder="请选择试卷" style="width:100%" filterable>
        <el-option v-for="p in paperOptions" :key="p.id" :label="p.name" :value="p.id" />
      </el-select>
      <template #footer>
        <el-button @click="batchAddVisible = false">取消</el-button>
        <el-button type="primary" :disabled="!targetPaperId" :loading="addToPaperLoading" @click="confirmBatchAdd">确定加入</el-button>
      </template>
    </el-dialog>
    </div><!-- /main-content -->
  </div><!-- /page-wrap -->
</template>

<script>
import request from '../../utils/request'
import teacherRequest from '../../utils/teacherRequest'
import RichEditor from '../../components/RichEditor.vue'
import filterLoader from '../../mixins/filterLoader'
import apiContext from '../../mixins/apiContext'
import { renderHtml, renderOptionHtml, normalizeTableHtml, cleanEditorHtml, hasBlockDiagramInHtml as detectBlockDiagramInHtml } from '../../utils/renderHtml'
import {
  defaultSubQuestion,
  relabelSubQuestions,
  parseSubQuestionsJson,
  calcSubQuestionTotalScore,
  buildFillQuestionPayload,
  validateSubQuestions,
  buildFillListPreviewHtml,
  getFillSubQuestionSummary
} from '../../utils/questionSubQuestions'
import { parseQuestionOptions } from '../../utils/questionOptions'
import { canTeacherEditQuestion as checkTeacherEditQuestion } from '../../utils/teacherOwnership'
import { getLeafTagNames } from '../../utils/tagDisplay'

export default {
  name: 'QuestionList',
  mixins: [filterLoader, apiContext],
  components: { RichEditor },
  props: {
    pickerMode: { type: Boolean, default: false },
    handoutId: { type: [String, Number], default: null },
    existingQuestionIds: { type: Array, default: () => [] },
    initialGradeId: { type: Number, default: null },
    initialSubjectId: { type: Number, default: null }
  },
  data() {
    return {
      filterForm: { gradeId: null, subjectId: null, type: null, status: null, keyword: '',
                       semester: null, examType: null, region: null, schoolYear: null,
                       knowledgeTagNodeId: null, questionTagNodeId: null, tagFilter: null },
      grades: [],
      subjects: [],
      formPapers: [],
      semesters: [],
      examTypes: [],
      regions: [],
      schoolYears: [],
      // 知识点标签树数据
      knowledgeTreeData: [],
      knowledgeExpandedKeys: [],
      selectedKnowledgeNode: null,
      activeTagTab: 'knowledge', // 'knowledge' | 'question'
      // 题型标签树数据
      questionTagTreeData: [],
      questionTagExpandedKeys: [],
      selectedQuestionTagNode: null,
      // 侧边栏状态
      tagTreeSearch: '',
      questionTagSearch: '',
      tagTreeLoading: false,
      // 学科-学段-年级联动
      selectedSubjectId: null,
      selectedStageId: null,
      stages: [],
      // 编辑弹窗标签选择器数据
      tagPicker: { kL1: null, kL2: null, kL3: null, qL1: null, qL2: null, qL3: null },
      tagPickerKL1: [],  // 知识点标签一级
      tagPickerKL2: [],  // 二级
      tagPickerKL3: [],  // 三级
      tagPickerQL1: [],  // 题型标签一级
      tagPickerQL2: [],
      tagPickerQL3: [],
      tableData: [],
      page: 0,
      size: 10,
      total: 0,
      loading: false,
      dialogVisible: false,
      editMode: false,
      saving: false,
      editForm: this.defaultForm(),
      typeNameMap: { SINGLE: '单选题', MULTI: '多选题', FILL: '填空题', QA: '问答题' },
      typeTagMap: { SINGLE: '', MULTI: 'warning', FILL: 'success', QA: 'danger' },
      // 学科关联章节（二级联动）
      subjectChapters: [],
      regionPresets: [],
      schoolYearPresets: [],
      // 组卷相关
      batchSelectedIds: [],
      // 组卷对话框
      composePaperVisible: false,
      composeQuestions: [],
      composeSelectedIds: [],
      composLoading: false,
      composePage: 0,
      composeSize: 20,
      composeTotal: 0,
      // 单题快捷加入
      quickAddVisible: false,
      quickAddSingleId: null,
      // 批量加入对话框
      batchAddVisible: false,
      // 共用
      targetPaperId: null,
      paperOptions: [],
      addToPaperLoading: false,
      // 快捷打标签
      tagQuickVisible: false,
      tagQuickRow: null,
      tagQuickSaving: false,
      tagQuick: { kL1: null, kL2: null, kL3: null, qL1: null, qL2: null, qL3: null },
      tagQuickKL2: [], tagQuickKL3: [],
      tagQuickQL2: [], tagQuickQL3: [],
      tagQuickKItems: [],
      tagQuickQItems: [],
      importTask: {
        taskId: '',
        running: false,
        filename: '',
        progress: 0,
        processed: 0,
        total: 0,
        successCount: 0,
        skipCount: 0,
        errorCount: 0
      },
      importPollTimer: null,
      teacherMe: null,
      regionTree: [],
      regionPicker: { l1: null, l2: null, l3: null },
      formNameFallback: { gradeName: '', subjectName: '' },
      handoutBasketLoading: false
    }
  },
  computed: {
    isPickerMode() {
      return this.pickerMode
    },
    pickerSelectedCount() {
      return this.batchSelectedIds.filter(id => !this.isExistingHandoutQuestion(id)).length
    },
    deletableBatchCount() {
      if (!this.isTeacherMode) return this.batchSelectedIds.length
      return this.batchSelectedIds.filter(id => {
        const row = this.tableData.find(r => r.id === id)
        return row && this.canTeacherEditQuestion(row)
      }).length
    },
    importUrl() { return `${this.apiPrefix}/question/importExcelAsync` },
    uploadHeaders() { return { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } },
    uploadServer() { return `${this.apiPrefix}/upload/image` },
    regionL2Options() {
      if (!this.regionPicker.l1) return []
      const l1 = this.regionTree.find(r => r.id === this.regionPicker.l1)
      return (l1 && l1.children) ? l1.children : []
    },
    regionL3Options() {
      if (!this.regionPicker.l2) return []
      const l2 = this.regionL2Options.find(c => c.id === this.regionPicker.l2)
      return (l2 && l2.children) ? l2.children : []
    },
    // 根据选择的学段过滤年级（列表页筛选）
    filteredGrades() {
      if (!this.selectedStageId) return this.grades
      return this.grades.filter(g => Number(g.stageId) === Number(this.selectedStageId))
    },
    isChoiceQuestion() {
      return this.editForm.type === 'SINGLE' || this.editForm.type === 'MULTI'
    },
    editRules() {
      return {
        gradeId: [{ required: true, message: '请选择年级' }],
        subjectId: [{ required: true, message: '请选择学科' }],
        type: [{ required: true, message: '请选择题型' }],
        content: [{ required: !this.isFillSubMode, message: this.isFillSubMode ? '请填写共用材料' : '请输入题目内容' }],
        answer: [{ required: !this.isFillSubMode, message: '请填写正确答案' }]
      }
    },
    isFillSubMode() {
      return this.editForm.type === 'FILL' && this.editForm.subQuestions && this.editForm.subQuestions.length > 0
    },
    fillContentLabel() {
      return this.isFillSubMode ? '共用材料' : '题目内容'
    },
    fillContentTip() {
      return this.isFillSubMode
        ? '填写大题共用背景、图表、参考数据等；各小问在下方子题中录入'
        : '空位可用下划线 _____ 或括号 （    ） 表示；多小问请点「添加子题」'
    },
    subQuestionTotalScore() {
      return calcSubQuestionTotalScore(this.editForm.subQuestions)
    },
    mergedFillAnswerPreview() {
      return buildFillQuestionPayload({
        materialHtml: this.editForm.content,
        subQuestions: this.editForm.subQuestions,
        mainAnalysis: this.editForm.analysis
      }).answer
    },
    contentPlaceholder() {
      if (this.editForm.type === 'FILL') {
        return this.isFillSubMode
          ? '请输入大题共用材料，可插入流程图、表格、参考数据等'
          : '请输入填空题题干，空位可用 _____ 或 （    ） 表示'
      }
      return '请输入题目内容，支持图片、公式、格式等'
    },
    answerPlaceholder() {
      if (this.editForm.type === 'MULTI') return '多选填 AB 或 ABC'
      if (this.editForm.type === 'FILL') return '填空答案，多个空用 | 分隔'
      return '单选填 A'
    }
  },
  created() {
    const bootstrapList = () => {
      if (this.isPickerMode) {
        this.applyPickerInitialFilters()
      }
      this.loadTagTrees()
      this.loadRegionTree()
      this.loadList()
    }
    if (this.isTeacherMode) {
      teacherRequest.get('/me').then(me => {
        this.teacherMe = me
        this.selectedSubjectId = me.subjectId
        this.filterForm.subjectId = me.subjectId
        this.loadFilters()
        this.loadAllGrades().then(bootstrapList)
      }).catch(() => {})
    } else {
      this.loadFilters()
      this.loadAllGrades().then(bootstrapList)
    }
  },
  beforeDestroy() {
    this.stopImportPolling()
  },
  watch: {
    tagTreeSearch(val) {
      this.$refs.knowledgeTree && this.$refs.knowledgeTree.filter(val)
    },
    questionTagSearch(val) {
      this.$refs.questionTree && this.$refs.questionTree.filter(val)
    }
  },
  methods: {
    isExistingHandoutQuestion(id) {
      return this.existingQuestionIds.some(item => Number(item) === Number(id))
    },
    applyPickerInitialFilters() {
      this.filterForm.status = 1
      if (this.initialSubjectId) {
        this.selectedSubjectId = this.initialSubjectId
        this.filterForm.subjectId = this.initialSubjectId
      }
      if (this.initialGradeId) {
        this.filterForm.gradeId = this.initialGradeId
        const grade = this.grades.find(g => Number(g.id) === Number(this.initialGradeId))
        if (grade && grade.stageId) {
          this.selectedStageId = grade.stageId
        }
      }
    },
    addSelectedToHandout(insertToContent) {
      const selectedRows = this.tableData.filter(row => this.batchSelectedIds.includes(row.id))
      const questionIds = selectedRows
        .map(row => row.id)
        .filter(id => !this.isExistingHandoutQuestion(id))
      if (!questionIds.length) {
        this.$message.warning('所选题目已在讲义中')
        return
      }
      this.handoutBasketLoading = true
      const handoutBasePath = this.isTeacherMode ? '/teacher/handout' : '/handout'
      this.api.post(`/handout/${this.handoutId}/questions/batch`, { questionIds }).then(data => {
        const added = data && data.added != null ? data.added : questionIds.length
        const insertedRows = selectedRows.filter(row => questionIds.includes(row.id))
        if (insertToContent) {
          sessionStorage.setItem(
            `handout:${this.handoutId}:pendingInsert`,
            JSON.stringify(insertedRows)
          )
        } else {
          sessionStorage.setItem(`handout:${this.handoutId}:pickerAdded`, '1')
        }
        this.$message.success(insertToContent ? `已加入 ${added} 题，返回后插入正文` : `已加入 ${added} 题`)
        this.$router.push(`${handoutBasePath}/${this.handoutId}`)
      }).catch(err => {
        this.$message.error((err && err.message) || '加入试题篮失败')
      }).finally(() => {
        this.handoutBasketLoading = false
      })
    },
    renderHtml,
    renderOptionHtml,
    getFillSubSummary(row) {
      return getFillSubQuestionSummary(row)
    },
    getQuestionPreviewHtml(row) {
      if (row.type === 'FILL') {
        return buildFillListPreviewHtml(row)
      }
      return row.content || ''
    },
    paperOptionLabel(paper) {
      if (!paper) return ''
      const title = paper.title || paper.name || ''
      const count = paper.questionCount != null ? `（${paper.questionCount}题）` : ''
      return `${title}${count}`
    },
    hasTableContent(html) {
      return html && /<table[\s>]/i.test(html)
    },
    hasInlineImages(html) {
      return html && /<img[\s>]/i.test(html)
    },
    hasBlockDiagramInHtml(html) {
      return detectBlockDiagramInHtml(html)
    },
    // 解析选项 JSON（保留纯图片/截图选项）
    parseOptions: parseQuestionOptions,
    defaultForm() {
      return {
        id: null,
        gradeId: null, subjectId: null, paperId: null, addToPaperId: null,
        type: 'SINGLE',
        content: '', answer: '', analysis: '',
        subQuestions: [],
        knowledgeTagItems: [],   // [{tagId, pathName}]
        questionTagItems: [],    // [{tagId, pathName}]
        semester: '', examType: '', region: '', regionCity: '', regionSchool: '', schoolYear: '', source: '',
        difficulty: 2,
        options: [{ key: 'A', value: '' }, { key: 'B', value: '' }, { key: 'C', value: '' }, { key: 'D', value: '' }],
        status: 1
      }
    },
    setFilter(key, val) {
      this.filterForm[key] = val
      this.page = 0
      this.loadList()
    },
    setTagFilter(val) {
      this.filterForm.tagFilter = val
      if (val === 'untagged') {
        this.filterForm.knowledgeTagNodeId = null
        this.filterForm.questionTagNodeId = null
        this.selectedKnowledgeNode = null
        this.selectedQuestionTagNode = null
        this.$refs.knowledgeTree && this.$refs.knowledgeTree.setCurrentKey(null)
        this.$refs.questionTree && this.$refs.questionTree.setCurrentKey(null)
      }
      this.page = 0
      this.loadList()
    },
    loadFilters() {
      this.loadDictType('subject', 'subjects')
      this.loadDictType('region', 'regions')
      this.loadStageDictBundle(this.selectedStageId)
      // 加载学段数据
      this.loadStages()
      // 加载所有年级数据（用于表单中学段-年级联动）
      this.loadAllGrades()
    },
    loadRegionTree() {
      this.api.get('/dict/region-tree').then(data => {
        this.regionTree = data || []
        if (this.dialogVisible) this.syncRegionPickerFromForm()
      }).catch(() => { this.regionTree = [] })
    },
    resetRegionPicker() {
      this.regionPicker = { l1: null, l2: null, l3: null }
    },
    syncRegionPickerFromForm() {
      this.resetRegionPicker()
      const { region, regionCity, regionSchool } = this.editForm
      if (!region || !this.regionTree.length) return
      const l1 = this.regionTree.find(r => r.name === region)
      if (!l1) return
      this.regionPicker.l1 = l1.id
      if (!regionCity) return
      const l2 = (l1.children || []).find(c => c.name === regionCity)
      if (!l2) return
      this.regionPicker.l2 = l2.id
      if (!regionSchool) return
      const l3 = (l2.children || []).find(s => s.name === regionSchool)
      if (l3) this.regionPicker.l3 = l3.id
    },
    onRegionL1Change(id) {
      if (!id) {
        this.editForm.region = ''
        this.editForm.regionCity = ''
        this.editForm.regionSchool = ''
      } else {
        const node = this.regionTree.find(r => r.id === id)
        this.editForm.region = node ? node.name : ''
        this.editForm.regionCity = ''
        this.editForm.regionSchool = ''
      }
      this.regionPicker.l2 = null
      this.regionPicker.l3 = null
      this.generateQuestionSource()
    },
    onRegionL2Change(id) {
      if (!id) {
        this.editForm.regionCity = ''
        this.editForm.regionSchool = ''
      } else {
        const node = this.regionL2Options.find(c => c.id === id)
        this.editForm.regionCity = node ? node.name : ''
        this.editForm.regionSchool = ''
      }
      this.regionPicker.l3 = null
      this.generateQuestionSource()
    },
    onRegionL3Change(id) {
      if (!id) {
        this.editForm.regionSchool = ''
      } else {
        const node = this.regionL3Options.find(s => s.id === id)
        this.editForm.regionSchool = node ? node.name : ''
      }
      this.generateQuestionSource()
    },
    onQuestionDialogOpened() {
      this.$nextTick(() => this.applyQuestionSourceAutoFill())
    },
    applyQuestionSourceAutoFill() {
      const { gradeId, subjectId, schoolYear, region, semester, examType } = this.editForm
      const hasMeta = gradeId && subjectId && (schoolYear || region || semester || examType)
      if (!hasMeta) return
      if (!this.editForm.source || !String(this.editForm.source).trim()) {
        this.generateQuestionSource()
      }
    },
    getGradeNameById(gradeId) {
      if (!gradeId) return ''
      const g = this.grades.find(x => Number(x.id) === Number(gradeId))
      return g ? g.name : (this.formNameFallback.gradeName || '')
    },
    getSubjectNameById(subjectId) {
      if (!subjectId) return ''
      if (this.isTeacherMode && this.teacherMe && this.teacherMe.subjectName) {
        return this.teacherMe.subjectName
      }
      const s = this.subjects.find(x => Number(x.id) === Number(subjectId))
      return s ? s.name : (this.formNameFallback.subjectName || '')
    },
    formatSemesterShort(semester) {
      if (!semester) return ''
      if (semester.includes('上')) return '（上）'
      if (semester.includes('下')) return '（下）'
      return `（${semester}）`
    },
    generateQuestionSource() {
      const { schoolYear, region, regionCity, regionSchool, gradeId, subjectId, semester, examType } = this.editForm
      const parts = []
      if (schoolYear) parts.push(String(schoolYear).trim())
      const regionText = [region, regionCity, regionSchool].filter(Boolean).map(v => String(v).trim()).join('')
      if (regionText) parts.push(regionText)
      const gradeName = this.getGradeNameById(gradeId)
      if (gradeName) parts.push(gradeName)
      const semShort = this.formatSemesterShort(semester)
      if (semShort) parts.push(semShort)
      if (examType) parts.push(String(examType).trim())
      const subjectName = this.getSubjectNameById(subjectId)
      if (subjectName) parts.push(subjectName)
      this.editForm.source = parts.length ? parts.join('') : ''
    },
    formatRegionLabel(row) {
      if (!row) return ''
      return [row.region, row.regionCity, row.regionSchool].filter(Boolean).join(' / ')
    },
    chapterDisplayName(chapter) {
      if (!chapter) return ''
      return chapter.displayName || chapter.name || ''
    },
    // 加载所有年级（用于表单）
    loadAllGrades() {
      return this.api.get('/dict', { params: { type: 'grade' } }).then(data => {
        this.grades = data || []
        return data
      })
    },
    // 根据学段加载年级（列表页筛选用）
    loadGradesByStage(stageId) {
      if (!stageId) {
        // 不清空 grades，因为表单需要所有年级数据
        return
      }
      // 年级数据已通过 loadAllGrades 加载，这里只做刷新
      return this.loadAllGrades()
    },
    loadStages() {
      // 根据选中的学科加载学段
      const params = {}
      if (this.selectedSubjectId) {
        params.subjectId = this.selectedSubjectId
      }
      this.api.get('/stage', { params }).then(data => {
        this.stages = data || []
      })
    },
    // 学科选择
    selectSubject(subjectId) {
      this.selectedSubjectId = subjectId
      this.selectedStageId = null
      this.loadStageDictBundle(null)
      this.filterForm.gradeId = null
      this.filterForm.subjectId = subjectId
      this.filterForm.knowledgeTagNodeId = null
      this.filterForm.questionTagNodeId = null
      this.grades = []
      // 清空标签树
      this.knowledgeTreeData = []
      this.questionTagTreeData = []
      this.selectedKnowledgeNode = null
      this.selectedQuestionTagNode = null
      // 重新加载学段（按学科筛选）
      this.loadStages()
      // 如果学科和学段都已选择，加载标签树
      if (this.selectedSubjectId && this.selectedStageId) {
        this.loadTagTrees()
      }
      this.page = 0
      this.loadList()
    },
    // 学段选择
    selectStage(stageId) {
      this.selectedStageId = stageId
      this.filterForm.gradeId = null
      this.filterForm.knowledgeTagNodeId = null
      this.filterForm.questionTagNodeId = null
      // 清空标签树
      this.knowledgeTreeData = []
      this.questionTagTreeData = []
      this.selectedKnowledgeNode = null
      this.selectedQuestionTagNode = null
      // 加载该学段下的年级
      this.loadGradesByStage(stageId)
      this.loadStageDictBundle(stageId)
      // 如果学科和学段都已选择，加载标签树
      if (this.selectedSubjectId && stageId) {
        this.loadTagTrees()
      }
      this.page = 0
      this.loadList()
    },
    // 年级选择
    selectGrade(gradeId) {
      this.filterForm.gradeId = gradeId
      this.page = 0
      this.loadList()
    },
    resolveFormTagScope() {
      const subjectId = Number(this.editForm.subjectId) || null
      let stageId = null
      if (this.editForm.gradeId) {
        const grade = this.grades.find(g => Number(g.id) === Number(this.editForm.gradeId))
        stageId = grade ? Number(grade.stageId) : null
      }
      return { subjectId, stageId }
    },
    resolveRowTagScope(row) {
      const subjectId = Number(row.subjectId || (row.subject && row.subject.id) || null) || null
      const gradeId = Number(row.gradeId || (row.grade && row.grade.id) || null) || null
      let stageId = null
      if (gradeId) {
        const grade = this.grades.find(g => Number(g.id) === Number(gradeId))
        stageId = grade ? Number(grade.stageId) : null
      }
      return { subjectId, stageId }
    },
    fetchTagTrees(subjectId, stageId) {
      if (!subjectId || !stageId) {
        return Promise.resolve({ knowledge: [], question: [] })
      }
      return Promise.all([
        this.api.get('/tag-tree', { params: { tagType: 'knowledge', subjectId, stageId } }),
        this.api.get('/tag-tree', { params: { tagType: 'question', subjectId, stageId } })
      ]).then(([kTree, qTree]) => ({
        knowledge: kTree || [],
        question: qTree || []
      }))
    },
    applyTagPickerTrees(knowledge, question) {
      this.tagPickerKL1 = knowledge
      this.tagPickerQL1 = question
      this.tagPickerKL2 = []
      this.tagPickerKL3 = []
      this.tagPickerQL2 = []
      this.tagPickerQL3 = []
    },
    async loadFormTagTrees() {
      const { subjectId, stageId } = this.resolveFormTagScope()
      if (!subjectId || !stageId) {
        this.applyTagPickerTrees([], [])
        return
      }
      const { knowledge, question } = await this.fetchTagTrees(subjectId, stageId)
      this.applyTagPickerTrees(knowledge, question)
    },
    loadTagTrees() {
      if (!this.selectedSubjectId || !this.selectedStageId) return
      this.tagTreeLoading = true
      this.fetchTagTrees(this.selectedSubjectId, this.selectedStageId).then(({ knowledge, question }) => {
        this.knowledgeTreeData = knowledge
        this.questionTagTreeData = question
        this.knowledgeExpandedKeys = knowledge.map(n => n.id)
        this.questionTagExpandedKeys = question.map(n => n.id)
        const { subjectId, stageId } = this.resolveFormTagScope()
        if (subjectId && stageId &&
            Number(subjectId) === Number(this.selectedSubjectId) &&
            Number(stageId) === Number(this.selectedStageId)) {
          this.tagPickerKL1 = knowledge
          this.tagPickerQL1 = question
        }
      }).finally(() => { this.tagTreeLoading = false })
    },
    // 侧边栏知识点树相关
    filterKnowledgeNode(value, data) {
      if (!value) return true
      return data.name.toLowerCase().includes(value.toLowerCase())
    },
    onKnowledgeNodeClick(data, node) {
      this.selectedKnowledgeNode = data
      this.filterForm.tagFilter = null
      this.filterForm.knowledgeTagNodeId = data.id
      this.page = 0
      this.loadList()
    },
    clearKnowledgeFilter() {
      this.selectedKnowledgeNode = null
      this.filterForm.knowledgeTagNodeId = null
      this.$refs.knowledgeTree && this.$refs.knowledgeTree.setCurrentKey(null)
      this.page = 0
      this.loadList()
    },
    clearCurrentTagFilter() {
      if (this.activeTagTab === 'knowledge') {
        this.clearKnowledgeFilter()
      } else {
        this.clearQuestionTag()
      }
    },
    // 编辑弹窗标签选择器联动
    onTagPickerKL1Change(val) {
      this.tagPicker.kL2 = null; this.tagPicker.kL3 = null
      this.tagPickerKL2 = []; this.tagPickerKL3 = []
      if (!val) return
      const node = this.tagPickerKL1.find(n => n.id === val)
      this.tagPickerKL2 = (node && node.children) ? node.children : []
    },
    onTagPickerKL2Change(val) {
      this.tagPicker.kL3 = null; this.tagPickerKL3 = []
      if (!val) return
      const node = this.tagPickerKL2.find(n => n.id === val)
      this.tagPickerKL3 = (node && node.children) ? node.children : []
    },
    onTagPickerQL1Change(val) {
      this.tagPicker.qL2 = null; this.tagPicker.qL3 = null
      this.tagPickerQL2 = []; this.tagPickerQL3 = []
      if (!val) return
      const node = this.tagPickerQL1.find(n => n.id === val)
      this.tagPickerQL2 = (node && node.children) ? node.children : []
    },
    onTagPickerQL2Change(val) {
      this.tagPicker.qL3 = null; this.tagPickerQL3 = []
      if (!val) return
      const node = this.tagPickerQL2.find(n => n.id === val)
      this.tagPickerQL3 = (node && node.children) ? node.children : []
    },
    addKnowledgeTag() {
      const l3id = this.tagPicker.kL3
      if (!l3id) return
      const already = this.editForm.knowledgeTagItems.some(i => i.tagId === l3id)
      if (already) { this.$message.warning('该标签已添加'); return }
      const l3 = this.tagPickerKL3.find(n => n.id === l3id)
      const l2 = this.tagPickerKL2.find(n => n.id === this.tagPicker.kL2)
      const l1 = this.tagPickerKL1.find(n => n.id === this.tagPicker.kL1)
      const pathName = [l1 && l1.name, l2 && l2.name, l3 && l3.name].filter(Boolean).join('/')
      this.editForm.knowledgeTagItems.push({ tagId: l3id, pathName })
    },
    removeKnowledgeTag(idx) {
      this.editForm.knowledgeTagItems.splice(idx, 1)
    },
    addQuestionTag() {
      const l3id = this.tagPicker.qL3
      if (!l3id) return
      const already = this.editForm.questionTagItems.some(i => i.tagId === l3id)
      if (already) { this.$message.warning('该标签已添加'); return }
      const l3 = this.tagPickerQL3.find(n => n.id === l3id)
      const l2 = this.tagPickerQL2.find(n => n.id === this.tagPicker.qL2)
      const l1 = this.tagPickerQL1.find(n => n.id === this.tagPicker.qL1)
      const pathName = [l1 && l1.name, l2 && l2.name, l3 && l3.name].filter(Boolean).join('/')
      this.editForm.questionTagItems.push({ tagId: l3id, pathName })
    },
    removeQuestionTag(idx) {
      this.editForm.questionTagItems.splice(idx, 1)
    },
    resetTagPicker() {
      this.tagPicker = { kL1: null, kL2: null, kL3: null, qL1: null, qL2: null, qL3: null }
      this.tagPickerKL2 = []; this.tagPickerKL3 = []
      this.tagPickerQL2 = []; this.tagPickerQL3 = []
    },
    // 回显已关联标签（编辑时）
    loadQuestionTagItems(questionId) {
      this.api.get(`/question/${questionId}/tags`).then(data => {
        this.editForm.knowledgeTagItems = (data.knowledgeTags || []).map(t => ({ tagId: t.tagId, pathName: t.pathName }))
        this.editForm.questionTagItems  = (data.questionTags  || []).map(t => ({ tagId: t.tagId, pathName: t.pathName }))
      }).catch(() => {})
    },
    // 题型标签树相关
    filterQuestionTagNode(value, data) {
      if (!value) return true
      return data.name.toLowerCase().includes(value.toLowerCase())
    },
    onQuestionTagNodeClick(data, node) {
      this.selectedQuestionTagNode = data
      this.filterForm.tagFilter = null
      this.filterForm.questionTagNodeId = data.id
      this.page = 0
      this.loadList()
    },
    clearQuestionTag() {
      this.selectedQuestionTagNode = null
      this.filterForm.questionTagNodeId = null
      this.$refs.questionTree && this.$refs.questionTree.setCurrentKey(null)
      this.page = 0
      this.loadList()
    },
    onFormGradeChange() {
      this.editForm.paperId = null
      this.editForm.addToPaperId = null
      this.editForm.knowledgeTagItems = []
      this.editForm.questionTagItems = []
      this.loadFormPapersChapters()
      this.generateQuestionSource()
      this.resetTagPicker()
      this.loadFormTagTrees()
    },
    onFormSubjectChange() {
      this.editForm.paperId = null
      this.editForm.addToPaperId = null
      this.editForm.knowledgeTagItems = []
      this.editForm.questionTagItems = []
      this.loadFormPapersChapters()
      this.generateQuestionSource()
      this.resetTagPicker()
      this.loadFormTagTrees()
    },
    loadFormPapersChapters() {
      const { gradeId, subjectId } = this.editForm
      if (!gradeId || !subjectId) { this.formPapers = []; return Promise.resolve() }
      return this.api.get('/question/filters', { params: { gradeId, subjectId } }).then(data => {
        this.formPapers = data.papers || []
        this.restoreAddToPaperSelection()
      })
    },
    addToPaperStorageKey() {
      return this.isTeacherMode ? 'teacher:question:lastAddToPaper' : 'admin:question:lastAddToPaper'
    },
    rememberAddToPaperSelection(paperId) {
      const ctx = {
        gradeId: this.editForm.gradeId,
        subjectId: this.editForm.subjectId,
        addToPaperId: paperId || null
      }
      try {
        sessionStorage.setItem(this.addToPaperStorageKey(), JSON.stringify(ctx))
      } catch (e) {}
    },
    restoreAddToPaperSelection() {
      try {
        const raw = sessionStorage.getItem(this.addToPaperStorageKey())
        if (!raw) return
        const ctx = JSON.parse(raw)
        if (!ctx || ctx.addToPaperId == null) return
        if (Number(ctx.gradeId) !== Number(this.editForm.gradeId)) return
        if (Number(ctx.subjectId) !== Number(this.editForm.subjectId)) return
        const exists = this.formPapers.some(p => Number(p.id) === Number(ctx.addToPaperId))
        if (exists) {
          this.editForm.addToPaperId = Number(ctx.addToPaperId)
        }
      } catch (e) {}
    },
    resetFormForContinueAdd() {
      const preserved = {
        gradeId: this.editForm.gradeId,
        subjectId: this.editForm.subjectId,
        addToPaperId: this.editForm.addToPaperId,
        semester: this.editForm.semester,
        examType: this.editForm.examType,
        schoolYear: this.editForm.schoolYear,
        region: this.editForm.region,
        regionCity: this.editForm.regionCity,
        regionSchool: this.editForm.regionSchool,
        type: this.editForm.type,
        difficulty: this.editForm.difficulty,
        status: this.editForm.status
      }
      this.editForm = { ...this.defaultForm(), ...preserved }
      this.resetTagPicker()
      this.syncRegionPickerFromForm()
      this.generateQuestionSource()
      this.$nextTick(() => {
        if (this.$refs.editForm) this.$refs.editForm.clearValidate()
      })
    },
    loadList() {
      this.loading = true
      const { gradeId, subjectId, type, status, keyword, semester, examType, region, schoolYear, knowledgeTagNodeId, questionTagNodeId, tagFilter } = this.filterForm
      // 传递学段ID用于筛选
      const stageId = this.selectedStageId
      this.api.get('/question/list', {
        params: { page: this.page, size: this.size, gradeId, subjectId, stageId, type, status, keyword, semester, examType, region, schoolYear, knowledgeTagNodeId, questionTagNodeId, tagFilter }
      }).then(data => {
        this.tableData = data.content || []
        this.total = data.totalElements || 0
      }).catch(() => {
        this.tableData = []
        this.total = 0
      }).finally(() => { this.loading = false })
    },
    onFilterChange() { this.page = 0; this.loadList() },
    onSubjectChange(subjectId) {
      this.filterForm.subjectId = subjectId
      this.filterForm.chapterId = null
      this.loadSubjectChapters()
      this.onFilterChange()
    },
    onGradeChange(gradeId) {
      this.filterForm.gradeId = gradeId
      this.filterForm.chapterId = null
      this.loadSubjectChapters()
      this.onFilterChange()
    },
    loadSubjectChapters() {
      const { subjectId, gradeId } = this.filterForm
      if (!subjectId || !gradeId) {
        this.subjectChapters = []
        return
      }
      this.api.get('/chapter', { params: { subjectId, gradeId } }).then(data => {
        this.subjectChapters = data || []
      })
    },
    resetFilter() {
      this.filterForm = { gradeId: null, subjectId: null, chapterId: null, type: null, status: null, keyword: '',
                            semester: null, examType: null, region: null, schoolYear: null,
                            knowledgeTagNodeId: null, questionTagNodeId: null, tagFilter: null }
      this.selectedKnowledgeNode = null
      this.selectedQuestionTagNode = null
      this.subjectChapters = []
      this.selectedSubjectId = null
      this.selectedStageId = null
      this.grades = []
      this.knowledgeTreeData = []
      this.questionTagTreeData = []
      this.$refs.knowledgeTree && this.$refs.knowledgeTree.setCurrentKey(null)
      this.$refs.questionTree && this.$refs.questionTree.setCurrentKey(null)
      this.onFilterChange()
    },
    handlePageChange(val) { this.page = val - 1; this.loadList() },
    handleSizeChange(val) { this.size = val; this.page = 0; this.loadList() },
    async openAddDialog() {
      this.editMode = false
      this.editForm = this.defaultForm()
      this.formNameFallback = { gradeName: '', subjectName: '' }
      if (this.isTeacherMode && this.teacherMe && this.teacherMe.subjectId) {
        this.editForm.subjectId = this.teacherMe.subjectId
        this.formNameFallback.subjectName = this.teacherMe.subjectName || ''
      }
      if (this.filterForm.gradeId) {
        this.editForm.gradeId = Number(this.filterForm.gradeId)
      }
      if (this.filterForm.subjectId && !this.isTeacherMode) {
        this.editForm.subjectId = Number(this.filterForm.subjectId)
      }
      this.formPapers = []
      if (!this.grades.length) {
        await this.loadAllGrades()
      }
      if (this.editForm.gradeId && this.editForm.subjectId) {
        this.loadFormPapersChapters()
      }
      this.resetTagPicker()
      await this.loadFormTagTrees()
      this.resetRegionPicker()
      this.dialogVisible = true
    },
    canTeacherEditQuestion(row) {
      if (!this.isTeacherMode) return true
      return checkTeacherEditQuestion(row, this.teacherMe)
    },
    async openEditDialog(row) {
      if (!this.canTeacherEditQuestion(row)) {
        this.$message.warning('管理员录入的题目不可修改')
        return
      }
      this.editMode = true
      const options = (() => { try { return JSON.parse(row.optionsJson || '[]') } catch { return [] } })()
      // 确保年级和学科ID正确获取（转为数字类型确保匹配）
      const gradeId = Number(row.gradeId || (row.grade && row.grade.id) || null) || null
      const subjectId = Number(row.subjectId || (row.subject && row.subject.id) || null) || null
      const paperId = row.paperId || (row.paper && row.paper.id) || null
      
      // 确保年级数据已加载
      if (!this.grades.length) {
        await this.loadAllGrades()
      }
      
      // 根据年级获取学段ID
      const grade = this.grades.find(g => Number(g.id) === Number(gradeId))
      const stageId = grade ? Number(grade.stageId) : null
      
      // 设置学科和学段，用于加载标签选择器数据
      this.selectedSubjectId = subjectId
      this.selectedStageId = stageId
      
      this.editForm = {
        id: row.id,
        gradeId: gradeId,
        subjectId: subjectId,
        paperId: paperId,
        addToPaperId: null,
        type: row.type,
        content: cleanEditorHtml(row.content || ''),
        answer: row.answer,
        analysis: cleanEditorHtml(row.analysis || ''),
        subQuestions: [],
        knowledgeTagItems: [],
        questionTagItems: [],
        semester: row.semester || '',
        examType: row.examType || '',
        region: row.region || '',
        regionCity: row.regionCity || '',
        regionSchool: row.regionSchool || '',
        schoolYear: row.schoolYear || '',
        source: row.source || '',
        difficulty: this.normalizeDifficulty(row.difficulty),
        options: (() => {
          if (row.type === 'FILL' || row.type === 'QA') return []
          return (options.length ? options : this.defaultForm().options).map(opt => ({
            ...opt,
            value: cleanEditorHtml(opt.value || '')
          }))
        })(),
        status: row.status
      }
      if (row.type === 'FILL' && row.subQuestionsJson) {
        const parsed = parseSubQuestionsJson(row.subQuestionsJson)
        if (parsed.items && parsed.items.length) {
          this.editForm.subQuestions = parsed.items
          this.editForm.content = cleanEditorHtml(parsed.material || this.editForm.content)
        }
      }
      this.formNameFallback = {
        gradeName: (row.grade && row.grade.name) || '',
        subjectName: (row.subject && row.subject.name) || ''
      }
      this.resetTagPicker()
      
      // 加载学段列表（用于标签选择器）
      await this.loadStages()
      if (stageId) {
        await this.loadStageDictBundle(stageId)
      }
      
      // 加载标签树数据（用于标签选择器）
      await this.loadFormTagTrees()
      
      this.loadFormPapersChapters()
      // 回显已关联标签
      this.loadQuestionTagItems(row.id)
      if (!this.regionTree.length) {
        await new Promise(resolve => {
          this.api.get('/dict/region-tree').then(data => {
            this.regionTree = data || []
            resolve()
          }).catch(() => { this.regionTree = []; resolve() })
        })
      }
      this.syncRegionPickerFromForm()
      this.dialogVisible = true
    },
    resetForm() {
      this.$refs.editForm && this.$refs.editForm.resetFields()
      this.formPapers = []
    },
    addOption() {
      const idx = this.editForm.options.length
      this.editForm.options.push({ key: String.fromCharCode(65 + idx), value: '' })
    },
    addSubQuestion() {
      if (this.editForm.type !== 'FILL') return
      const next = defaultSubQuestion(this.editForm.subQuestions.length)
      this.editForm.subQuestions = relabelSubQuestions([...(this.editForm.subQuestions || []), next])
    },
    removeSubQuestion(index) {
      const list = [...(this.editForm.subQuestions || [])]
      list.splice(index, 1)
      this.editForm.subQuestions = relabelSubQuestions(list)
    },
    removeOption(i) {
      this.editForm.options.splice(i, 1)
      this.editForm.options.forEach((opt, idx) => { opt.key = String.fromCharCode(65 + idx) })
    },
    flushEditorContent() {
      if (this.$refs.contentEditor) {
        this.editForm.content = this.$refs.contentEditor.getHtml()
      }
      if (this.$refs.analysisEditor) {
        this.editForm.analysis = this.$refs.analysisEditor.getHtml()
      }
      const optionEditors = this.$refs.optionEditors
      if (optionEditors) {
        const list = Array.isArray(optionEditors) ? optionEditors : [optionEditors]
        list.forEach((editor, i) => {
          if (editor && this.editForm.options[i]) {
            this.editForm.options[i].value = editor.getHtml()
          }
        })
      }
      const subEditors = this.$refs.subContentEditors
      if (subEditors) {
        const list = Array.isArray(subEditors) ? subEditors : [subEditors]
        list.forEach((editor, i) => {
          if (editor && this.editForm.subQuestions[i]) {
            this.editForm.subQuestions[i].content = editor.getHtml()
          }
        })
      }
    },
    saveAndContinueQuestion() {
      this.saveQuestion({ continueAdd: true })
    },
    saveQuestion(options = {}) {
      const { continueAdd = false } = options
      this.$refs.editForm.validate(valid => {
        if (!valid) return
        this.flushEditorContent()
        if (this.isFillSubMode) {
          const subError = validateSubQuestions(this.editForm.subQuestions)
          if (subError) {
            this.$message.warning(subError)
            return
          }
        }
        this.saving = true
        const payload = { ...this.editForm }
        payload.content = normalizeTableHtml(payload.content)
        payload.analysis = normalizeTableHtml(payload.analysis)
        if (this.isFillSubMode) {
          const merged = buildFillQuestionPayload({
            materialHtml: payload.content,
            subQuestions: payload.subQuestions,
            mainAnalysis: payload.analysis
          })
          payload.content = normalizeTableHtml(merged.content)
          payload.answer = merged.answer
          payload.analysis = normalizeTableHtml(merged.analysis)
          payload.subQuestionsJson = merged.subQuestionsJson
        } else if (payload.type === 'FILL') {
          payload.subQuestionsJson = null
        }
        delete payload.subQuestions
        if (payload.options && payload.options.length) {
          payload.options = payload.options.map(opt => ({
            ...opt,
            value: normalizeTableHtml(opt.value)
          }))
        }
        // 三级联动标签：组装 IDs 和展示字符串
        payload.knowledgeTagIds = payload.knowledgeTagItems.map(i => i.tagId)
        payload.questionTagIds  = payload.questionTagItems.map(i => i.tagId)
        payload.knowledgeTags   = payload.knowledgeTagItems.map(i => i.pathName).join(',')
        payload.questionTag     = payload.questionTagItems.map(i => i.pathName).join(',')
        delete payload.knowledgeTagItems
        delete payload.questionTagItems
        if (payload.type === 'SINGLE' || payload.type === 'MULTI') {
          payload.optionsJson = JSON.stringify(payload.options)
        } else {
          payload.optionsJson = null
        }
        delete payload.options
        const addToPaperId = payload.addToPaperId || null
        delete payload.addToPaperId
        const req = this.editMode
          ? this.api.put(`/question/${payload.id}`, payload)
          : this.api.post('/question', payload)
        req.then(async (saved) => {
          const questionId = this.editMode ? payload.id : (saved && saved.id)
          let successMsg = this.editMode ? '修改成功' : '新增成功'
          if (addToPaperId && questionId) {
            try {
              const data = await this.addQuestionsToPaper(addToPaperId, [questionId])
              const added = data && data.added !== undefined ? data.added : 1
              successMsg += added > 0 ? '，已加入试卷' : '（该题已在试卷中）'
            } catch (e) {
              this.$message.warning(`${this.editMode ? '修改' : '新增'}成功，但加入试卷失败`)
              if (!continueAdd) this.dialogVisible = false
              this.loadList()
              return
            }
          }
          if (continueAdd && !this.editMode) {
            this.rememberAddToPaperSelection(addToPaperId)
            this.loadList()
            this.resetFormForContinueAdd()
            this.$message.success(`${successMsg}，可继续录入下一题`)
            return
          }
          this.$message.success(successMsg)
          this.dialogVisible = false
          this.loadList()
        }).catch(err => {
          this.$message.error((err && err.message) || '保存失败')
        }).finally(() => { this.saving = false })
      })
    },
    toggleStatus(row) {
      this.api.put(`/question/${row.id}/status`, { status: row.status === 1 ? 0 : 1 }).then(() => {
        this.$message.success('状态已更新')
        this.loadList()
      })
    },
    normalizeDifficulty(val) {
      const n = Number(val)
      if (!n || n < 2) return 2
      if (n > 5) return 5
      return n
    },
    onEditDifficultyChange(val) {
      this.editForm.difficulty = this.normalizeDifficulty(val)
    },
    onQuestionTypeChange(type) {
      if (type === 'SINGLE' || type === 'MULTI') {
        if (!this.editForm.options || !this.editForm.options.length) {
          this.editForm.options = this.defaultForm().options
        }
        this.editForm.subQuestions = []
        return
      }
      if (type === 'FILL' && /^[A-H]+$/i.test(String(this.editForm.answer || '').trim())) {
        this.editForm.answer = ''
      }
      if (type !== 'FILL') {
        this.editForm.subQuestions = []
      }
    },
    updateDifficulty(row, val) {
      if (!this.canTeacherEditQuestion(row)) {
        this.$message.warning('管理员录入的题目不可修改')
        return
      }
      const difficulty = this.normalizeDifficulty(val)
      const prev = this.normalizeDifficulty(row.difficulty)
      if (difficulty === prev) return
      this.api.put(`/question/${row.id}/difficulty`, { difficulty }).then(() => {
        row.difficulty = difficulty
        this.$message.success('难度已更新')
      }).catch(err => {
        this.$message.error((err && err.message) || '难度更新失败')
      })
    },
    deleteQuestion(row) {
      this.$confirm('确认删除该题目？此操作不可恢复', '警告', { type: 'warning' }).then(() => {
        this.api.delete(`/question/${row.id}`).then(() => {
          this.$message.success('已删除')
          this.loadList()
        })
      }).catch(() => {})
    },
    onImportSuccess(response) {
      const res = response.data || response
      const taskId = res && res.taskId
      if (!taskId) {
        this.$message.error((response && response.message) || '导入任务创建失败')
        return
      }

      this.importTask.taskId = taskId
      this.importTask.running = true
      this.importTask.progress = 0
      this.importTask.processed = 0
      this.importTask.total = 0
      this.importTask.successCount = 0
      this.importTask.skipCount = 0
      this.importTask.errorCount = 0
      this.$message.success('导入任务已提交，正在后台处理...')
      this.startImportPolling()
    },
    startImportPolling() {
      this.stopImportPolling()
      this.fetchImportTaskStatus()
      this.importPollTimer = setInterval(() => {
        this.fetchImportTaskStatus()
      }, 1500)
    },
    stopImportPolling() {
      if (this.importPollTimer) {
        clearInterval(this.importPollTimer)
        this.importPollTimer = null
      }
    },
    fetchImportTaskStatus() {
      if (!this.importTask.taskId) return

      this.api.get(`/question/importExcelTask/${this.importTask.taskId}`).then(task => {
        this.importTask.filename = task.filename || ''
        this.importTask.progress = task.progress || 0
        this.importTask.processed = task.processed || 0
        this.importTask.total = task.total || 0
        this.importTask.successCount = task.successCount || 0
        this.importTask.skipCount = task.skipCount || 0
        this.importTask.errorCount = task.errorCount || 0

        if (task.status === 'SUCCESS' || task.status === 'FAILED') {
          this.importTask.running = false
          this.stopImportPolling()
          this.handleImportTaskDone(task)
        }
      }).catch(() => {
        this.importTask.running = false
        this.stopImportPolling()
        this.$message.error('导入进度查询失败，请稍后刷新列表确认')
      })
    },
    handleImportTaskDone(task) {
      const count = task.successCount || 0
      const skipCount = task.skipCount || 0
      const errors = Array.isArray(task.errors) ? task.errors : []

      const showErrors = (title, list, type = 'warning') => {
        const html = `<div style="max-height:320px;overflow:auto;white-space:pre-wrap;line-height:1.6;">${list
          .slice(0, 12)
          .map((e, idx) => `${idx + 1}. ${e}`)
          .join('\n')}\n\n请下载最新导入模版后重试。</div>`
        this.$alert(html, title, { type, dangerouslyUseHTMLString: true })
      }

      if (count > 0) {
        const msg = skipCount > 0
          ? `导入完成：成功 ${count} 条，跳过 ${skipCount} 条`
          : `导入成功，共导入 ${count} 条题目`
        this.$message.success(msg)
        if (errors.length > 0) {
          showErrors(`部分行导入失败（共${errors.length}条）`, errors, 'warning')
        }
        this.loadList()
      } else {
        if (errors.length > 0) {
          showErrors('导入失败', errors, 'error')
        } else if (task.status === 'FAILED' && task.message) {
          this.$message.error(task.message)
        } else {
          this.$message.warning('未导入任何数据，请检查文件内容是否正确（确保有数据行）')
        }
      }
    },
    onImportError() { this.$message.error('导入失败，请检查文件格式') },
    downloadTemplate() {
      this.api.get('/question/template', { responseType: 'blob' }).then(blob => {
        const url = window.URL.createObjectURL(new Blob([blob]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'question_template.xlsx')
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      }).catch(() => { this.$message.error('模版下载失败') })
    },
    formatTime(t) {
      if (!t) return ''
      return t.replace('T', ' ').substring(0, 16)
    },
    // 组卷相关 - loadPaperList 由 mixin 提供
    // loadPaperList() 方法已由 mixin 提供，直接调用即可
    openComposePaperDialog() {
      this.composePaperVisible = true
    },
    onComposePaperOpen() {
      this.loadPaperList('paperOptions')
      this.loadComposeList()
    },
    loadComposeList() {
      this.composLoading = true
      const { gradeId, subjectId, type, status, keyword, semester, examType, region, schoolYear, knowledgeTagNodeId, questionTagNodeId } = this.filterForm
      this.api.get('/question/list', {
        params: { page: this.composePage, size: this.composeSize, gradeId, subjectId, type, status, keyword, semester, examType, region, schoolYear, knowledgeTagNodeId, questionTagNodeId }
      }).then(data => {
        this.composeQuestions = data.content || []
        this.composeTotal = data.totalElements || 0
      }).finally(() => { this.composLoading = false })
    },
    onComposePageChange(val) {
      this.composePage = val - 1
      this.composeSelectedIds = []
      this.loadComposeList()
    },
    toggleComposeSelect(id) {
      const idx = this.composeSelectedIds.indexOf(id)
      if (idx > -1) this.composeSelectedIds.splice(idx, 1)
      else this.composeSelectedIds.push(id)
    },
    toggleSelectAll(checked) {
      if (checked) {
        const allIds = this.composeQuestions.map(q => q.id)
        allIds.forEach(id => { if (!this.composeSelectedIds.includes(id)) this.composeSelectedIds.push(id) })
      } else {
        const pageIds = this.composeQuestions.map(q => q.id)
        this.composeSelectedIds = this.composeSelectedIds.filter(id => !pageIds.includes(id))
      }
    },
    addQuestionsToPaper(paperId, questionIds) {
      const path = this.isTeacherMode
        ? `/papers/${paperId}/questions/batch`
        : `/paper/${paperId}/questions/batch`
      return this.api.post(path, { questionIds })
    },
    resolvePaperIdForBatch(targetPaperId) {
      if (this.isTeacherMode) {
        return Promise.resolve(targetPaperId)
      }
      const paper = this.paperOptions.find(p => p.id === targetPaperId)
      if (!paper) return Promise.reject(new Error('未找到目标试卷'))
      return this.api.post('/paper/ensure', { name: paper.name }).then(data => data.id)
    },
    confirmComposePaper() {
      if (!this.targetPaperId || this.composeSelectedIds.length === 0) return
      this.addToPaperLoading = true
      this.resolvePaperIdForBatch(this.targetPaperId)
        .then(paperId => this.addQuestionsToPaper(paperId, this.composeSelectedIds))
        .then(data => {
          const added = data.added !== undefined ? data.added : this.composeSelectedIds.length
          this.$message.success(`已成功加入 ${added} 题（重复的自动跳过）`)
          this.composeSelectedIds = []
          this.composePaperVisible = false
        }).catch(() => {}).finally(() => { this.addToPaperLoading = false })
    },
    quickAddToPaper(row) {
      this.quickAddSingleId = row.id
      this.targetPaperId = null
      this.loadPaperList('paperOptions')
      this.quickAddVisible = true
    },
    confirmQuickAdd() {
      if (!this.targetPaperId) return
      this.addToPaperLoading = true
      this.resolvePaperIdForBatch(this.targetPaperId)
        .then(paperId => this.addQuestionsToPaper(paperId, [this.quickAddSingleId]))
        .then(data => {
          const added = data.added !== undefined ? data.added : 1
          this.$message.success(added > 0 ? '加入成功' : '该题已在试卷中')
          this.quickAddVisible = false
        }).catch(() => {}).finally(() => { this.addToPaperLoading = false })
    },
    toggleBatchSelect(id) {
      if (this.isPickerMode && this.isExistingHandoutQuestion(id)) return
      const idx = this.batchSelectedIds.indexOf(id)
      if (idx > -1) this.batchSelectedIds.splice(idx, 1)
      else this.batchSelectedIds.push(id)
    },
    toggleSelectAllPage(checked) {
      const pageIds = this.tableData
        .map(r => r.id)
        .filter(id => !(this.isPickerMode && this.isExistingHandoutQuestion(id)))
      if (checked) {
        pageIds.forEach(id => { if (!this.batchSelectedIds.includes(id)) this.batchSelectedIds.push(id) })
      } else {
        this.batchSelectedIds = this.batchSelectedIds.filter(id => !pageIds.includes(id))
      }
    },
    batchDelete() {
      const ids = this.isTeacherMode
        ? this.batchSelectedIds.filter(id => {
          const row = this.tableData.find(r => r.id === id)
          return row && this.canTeacherEditQuestion(row)
        })
        : this.batchSelectedIds.slice()
      if (ids.length === 0) return
      const skipped = this.batchSelectedIds.length - ids.length
      const msg = skipped > 0
        ? `确定删除选中的 ${ids.length} 道题目吗？（另有 ${skipped} 道管理员录入题目将跳过）`
        : `确定删除选中的 ${ids.length} 道题目吗？`
      this.$confirm(msg, '批量删除', {
        type: 'warning'
      }).then(() => {
        this.api.post('/question/batch-delete', { ids }).then(data => {
          this.$message.success(`成功删除 ${data.count} 道题目`)
          this.batchSelectedIds = []
          this.loadList()
        })
      }).catch(() => {})
    },
    openBatchAddDialog() {
      if (this.batchSelectedIds.length === 0) return
      this.targetPaperId = null
      this.loadPaperList('paperOptions')
      this.batchAddVisible = true
    },
    confirmBatchAdd() {
      if (!this.targetPaperId) return
      this.addToPaperLoading = true
      this.resolvePaperIdForBatch(this.targetPaperId)
        .then(paperId => this.addQuestionsToPaper(paperId, this.batchSelectedIds))
        .then(data => {
          const added = data.added !== undefined ? data.added : this.batchSelectedIds.length
          this.$message.success(`已成功加入 ${added} 题（重复的自动跳过）`)
          this.batchSelectedIds = []
          this.batchAddVisible = false
        }).catch(() => {}).finally(() => { this.addToPaperLoading = false })
    },
    // ===== 快捷打标签 =====
    // 卡片标签展示：只取每个路径的最后一节（三级名）
    getLeafTagNames,
    async openTagQuickDialog(row) {
      if (!this.canTeacherEditQuestion(row)) {
        this.$message.warning('管理员录入的题目不可修改')
        return
      }
      this.tagQuickRow = row
      this.tagQuick = { kL1: null, kL2: null, kL3: null, qL1: null, qL2: null, qL3: null }
      this.tagQuickKL2 = []; this.tagQuickKL3 = []
      this.tagQuickQL2 = []; this.tagQuickQL3 = []
      this.tagQuickKItems = []
      this.tagQuickQItems = []
      if (!this.grades.length) {
        await this.loadAllGrades()
      }
      const { subjectId, stageId } = this.resolveRowTagScope(row)
      if (subjectId && stageId) {
        const { knowledge, question } = await this.fetchTagTrees(subjectId, stageId)
        this.applyTagPickerTrees(knowledge, question)
      } else {
        this.applyTagPickerTrees([], [])
      }
      // 回显已有标签
      this.api.get(`/question/${row.id}/tags`).then(data => {
        this.tagQuickKItems = (data.knowledgeTags || []).map(t => ({ tagId: t.tagId, pathName: t.pathName }))
        this.tagQuickQItems = (data.questionTags  || []).map(t => ({ tagId: t.tagId, pathName: t.pathName }))
      }).catch(() => {})
      this.tagQuickVisible = true
    },
    resetTagQuickDialog() {
      this.tagQuickRow = null
      this.tagQuickKItems = []; this.tagQuickQItems = []
    },
    onQkKL1Change(val) {
      this.tagQuick.kL2 = null; this.tagQuick.kL3 = null
      this.tagQuickKL2 = []; this.tagQuickKL3 = []
      if (!val) return
      const node = this.tagPickerKL1.find(n => n.id === val)
      this.tagQuickKL2 = (node && node.children) ? node.children : []
    },
    onQkKL2Change(val) {
      this.tagQuick.kL3 = null; this.tagQuickKL3 = []
      if (!val) return
      const node = this.tagQuickKL2.find(n => n.id === val)
      this.tagQuickKL3 = (node && node.children) ? node.children : []
    },
    onQkQL1Change(val) {
      this.tagQuick.qL2 = null; this.tagQuick.qL3 = null
      this.tagQuickQL2 = []; this.tagQuickQL3 = []
      if (!val) return
      const node = this.tagPickerQL1.find(n => n.id === val)
      this.tagQuickQL2 = (node && node.children) ? node.children : []
    },
    onQkQL2Change(val) {
      this.tagQuick.qL3 = null; this.tagQuickQL3 = []
      if (!val) return
      const node = this.tagQuickQL2.find(n => n.id === val)
      this.tagQuickQL3 = (node && node.children) ? node.children : []
    },
    addTagQuickK() {
      const l3id = this.tagQuick.kL3; if (!l3id) return
      if (this.tagQuickKItems.some(i => i.tagId === l3id)) { this.$message.warning('该标签已添加'); return }
      const l3 = this.tagQuickKL3.find(n => n.id === l3id)
      const l2 = this.tagQuickKL2.find(n => n.id === this.tagQuick.kL2)
      const l1 = this.tagPickerKL1.find(n => n.id === this.tagQuick.kL1)
      const pathName = [l1&&l1.name, l2&&l2.name, l3&&l3.name].filter(Boolean).join('/')
      this.tagQuickKItems.push({ tagId: l3id, pathName })
    },
    addTagQuickQ() {
      const l3id = this.tagQuick.qL3; if (!l3id) return
      if (this.tagQuickQItems.some(i => i.tagId === l3id)) { this.$message.warning('该标签已添加'); return }
      const l3 = this.tagQuickQL3.find(n => n.id === l3id)
      const l2 = this.tagQuickQL2.find(n => n.id === this.tagQuick.qL2)
      const l1 = this.tagPickerQL1.find(n => n.id === this.tagQuick.qL1)
      const pathName = [l1&&l1.name, l2&&l2.name, l3&&l3.name].filter(Boolean).join('/')
      this.tagQuickQItems.push({ tagId: l3id, pathName })
    },
    saveTagQuick() {
      if (!this.tagQuickRow) return
      this.tagQuickSaving = true
      const row = this.tagQuickRow
      const knowledgeTagIds = this.tagQuickKItems.map(i => i.tagId)
      const questionTagIds  = this.tagQuickQItems.map(i => i.tagId)
      const knowledgeTags   = this.tagQuickKItems.map(i => i.pathName).join(',')
      const questionTag     = this.tagQuickQItems.map(i => i.pathName).join(',')
      // 只更新标签相关字段
      this.api.put(`/question/${row.id}/tags`, {
        knowledgeTagIds,
        questionTagIds,
        knowledgeTags,
        questionTag
      }).then(() => {
        // 更新卡片展示字段
        row.knowledgeTags = knowledgeTags
        row.questionTag   = questionTag
        this.$message.success('标签已更新')
        this.tagQuickVisible = false
      }).catch(() => {}).finally(() => { this.tagQuickSaving = false })
    }
  }
}
</script>

<style scoped>
/* ===== 顶部搜索栏 ===== */
.top-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.top-bar-right { display: flex; align-items: center; gap: 0; }

/* ===== 页面布局：左侧边栏 + 右侧主内容 ===== */
.page-wrap {
  display: flex;
  height: calc(100vh - 84px);
  overflow: hidden;
}
.sidebar {
  width: 280px;
  flex-shrink: 0;
  background: #fff;
  border-right: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Tab 标签栏 */
.sidebar-tabs {
  display: flex;
  align-items: center;
  border-bottom: 1px solid #e8e8e8;
  background: #fafafa;
}
.sidebar-tab {
  flex: 1;
  padding: 14px 0;
  text-align: center;
  font-size: 14px;
  color: #666;
  cursor: pointer;
  transition: all 0.15s;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
}
.sidebar-tab:hover {
  color: #1677ff;
}
.sidebar-tab.active {
  color: #1677ff;
  background: #fff;
  border-bottom-color: #1677ff;
  font-weight: 500;
}
.sidebar-tab-reset {
  padding: 0 12px;
  font-size: 12px;
}

/* 侧边栏内容区 */
.sidebar-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.sidebar-search {
  padding: 12px;
  border-bottom: 1px solid #f0f0f0;
}
.sidebar-tree {
  flex: 1;
  overflow-y: auto;
  padding: 4px 0;
}
.sidebar-tag-list {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  gap: 8px;
}
.sidebar-tag-item {
  padding: 6px 12px;
  background: #f5f5f5;
  border-radius: 4px;
  font-size: 13px;
  color: #666;
  cursor: pointer;
  transition: all 0.15s;
}
.sidebar-tag-item:hover {
  background: #e6f4ff;
  color: #1677ff;
}
.sidebar-tag-item.active {
  background: #1677ff;
  color: #fff;
}

/* 树节点样式 */
.tree-node {
  display: flex;
  align-items: center;
  flex: 1;
}
.tree-icon {
  width: 16px;
  height: 16px;
  font-size: 12px;
  color: #999;
  margin-right: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.tree-icon-leaf {
  width: 16px;
  height: 16px;
  margin-right: 4px;
}
.tree-label {
  flex: 1;
  font-size: 13px;
}
.tree-count {
  font-size: 11px;
  color: #999;
  background: #f5f5f5;
  padding: 0 6px;
  border-radius: 10px;
  margin-left: 4px;
}

/* 右侧主内容区 */
.main-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background: #f5f7fa;
}
.main-content .filter-card {
  margin-bottom: 16px;
}

/* ===== 行内筛选 ===== */
.filter-rows { display: flex; flex-direction: column; gap: 8px; }
.filter-row { display: flex; align-items: center; gap: 0; min-height: 32px; }
.filter-label {
  width: 42px;
  flex-shrink: 0;
  color: #888;
  font-size: 13px;
}
.filter-opts { display: flex; flex-wrap: wrap; gap: 2px; }
.filter-opt {
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  color: #555;
  transition: all 0.15s;
  user-select: none;
  white-space: nowrap;
}
.filter-opt:hover { color: #1677ff; background: #f0f5ff; }
.filter-opt.active {
  color: #1677ff;
  background: #e6f4ff;
  font-weight: 600;
}

/* ===== 列表元数据 ===== */
.list-meta {
  font-size: 13px;
  color: #666;
  margin: 12px 0 8px;
  display: flex;
  align-items: center;
}

/* ===== 题目卡片 ===== */
.question-list { display: flex; flex-direction: column; gap: 8px; }
.q-card {
  background: #fff;
  border-radius: 6px;
  border: 1px solid #eee;
  padding: 14px 16px;
  transition: box-shadow 0.2s;
}
.q-card:hover { box-shadow: 0 2px 10px rgba(0,0,0,0.08); border-color: #d0e6ff; }

.q-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
}
.q-difficulty {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}
.q-difficulty >>> .el-rate__icon {
  font-size: 16px;
  margin-right: 2px;
}
.q-num {
  width: 22px;
  height: 22px;
  background: #f0f2f5;
  color: #666;
  border-radius: 4px;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.tag-custom {
  color: #1677ff;
  background: #f0f5ff;
  border-color: #adc6ff;
}
.q-meta-tags { display: flex; gap: 4px; flex-wrap: wrap; }
.meta-chip {
  padding: 1px 6px;
  background: #f5f5f5;
  border-radius: 3px;
  font-size: 12px;
  color: #777;
}
.meta-chip.paper { background: #fff7e6; color: #d46b08; }
.meta-chip.exam { background: #f6ffed; color: #389e0d; }
.meta-chip.region { background: #f0f5ff; color: #1d39c4; max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: inline-block; vertical-align: middle; }
.region-cascade { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
.meta-chip.source { background: #fff7e6; color: #d48806; max-width: 280px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: inline-block; vertical-align: middle; }

.q-content {
  font-size: 14px;
  color: #333;
  line-height: 1.6;
  margin-bottom: 8px;
  /* 最多显示3行 */
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
}

/* 含表格的题目：完整展示，不截断行数 */
.q-content.has-table {
  display: block;
  -webkit-line-clamp: unset;
  overflow: visible;
  max-height: none;
}

/* 含行内图片：取消 line-clamp，避免图文被截断 */
.q-content.has-inline-images {
  display: block;
  -webkit-line-clamp: unset;
  overflow: visible;
  max-height: none;
}

/* 含示意图：完整展示，不裁切 */
.q-content.has-block-diagram ::v-deep img.block-diagram {
  max-width: 100%;
  height: auto !important;
  margin: 6px 0;
}

/* 行内化学式/小图保持与文字同行 */
.q-content.has-inline-images ::v-deep img.inline-formula {
  max-height: 3.2em;
  width: auto !important;
  vertical-align: middle;
}

/* 表格内图片不受行内小图 max-height 限制 */
.q-content ::v-deep table img,
.q-content.has-inline-images ::v-deep table img.inline-formula,
.q-content.has-block-diagram ::v-deep table img.block-diagram {
  display: inline-block !important;
  max-height: none !important;
  max-width: 100% !important;
  width: auto !important;
  height: auto !important;
  min-width: 24px;
  min-height: 24px;
  visibility: visible !important;
  opacity: 1 !important;
}

.q-content.fill-sub-preview {
  display: block;
  -webkit-line-clamp: unset;
  overflow: hidden;
  max-height: 120px;
}

.q-content.fill-sub-preview ::v-deep .fill-preview-material {
  margin-bottom: 6px;
  color: #374151;
}

.q-content.fill-sub-preview ::v-deep .fill-preview-sub {
  display: block;
  margin: 4px 0;
  padding-left: 8px;
  border-left: 3px solid #91d5ff;
  line-height: 1.7;
}

.q-content.fill-sub-preview ::v-deep .fill-preview-label {
  font-weight: 600;
  color: #1677ff;
  margin-right: 4px;
}

.q-content.fill-sub-preview ::v-deep .fill-preview-score {
  color: #6b7280;
  font-size: 12px;
  margin-right: 6px;
}

.q-content.fill-sub-preview ::v-deep .fill-preview-body u,
.q-content.fill-sub-preview ::v-deep .fill-preview-body {
  color: #111827;
}

/* 下划线空位更明显 */
.rich-html u,
.rich-html .fill-blank,
.q-content ::v-deep u,
.q-content ::v-deep .fill-blank {
  text-decoration: none;
  border-bottom: 1px solid #333;
  padding: 0 8px;
  min-width: 40px;
  display: inline-block;
  background: #fafafa;
}

.q-card .q-content.has-table {
  overflow-x: auto;
}

.q-options {
  margin-bottom: 8px;
  padding: 8px 12px;
  background: #fafafa;
  border-radius: 4px;
}

/* 选项含 p/img 块级标签；::v-deep 才能作用到 v-html 插入的图片 */
.q-options ::v-deep .rich-html p {
  display: inline;
  margin: 0;
}

.q-options ::v-deep .rich-html p:has(img) {
  display: inline;
  margin: 0;
}

.q-options ::v-deep .rich-html img,
.q-options ::v-deep .rich-html img.block-diagram,
.q-options ::v-deep .rich-html img.inline-formula {
  display: inline !important;
  vertical-align: middle;
  max-height: 1.55em !important;
  max-width: 100%;
  width: auto !important;
  height: auto !important;
  margin: 0 2px;
  object-fit: contain;
}

.q-option-item {
  display: flex;
  align-items: flex-start;
  padding: 4px 0;
  font-size: 13px;
  color: #555;
  line-height: 1.5;
}

.q-option-key {
  font-weight: 600;
  color: #333;
  min-width: 20px;
  margin-right: 4px;
}

.q-option-value {
  flex: 1;
  min-width: 0;
}

.q-option-value ::v-deep img {
  max-width: 100%;
  max-height: 1.55em !important;
  vertical-align: middle;
  object-fit: contain;
}

.q-knowledge-tags {
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px;
}

.q-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid #f5f5f5;
  padding-top: 8px;
  margin-top: 4px;
}
.q-time { font-size: 12px; color: #bbb; }
.q-actions { display: flex; align-items: center; }
.q-actions .el-divider--vertical { margin: 0 2px; }

/* ===== 分页 ===== */
.pagination { margin-top: 16px; text-align: right; }

/* ===== 组卷对话框 ===== */
.compose-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}
.compose-select-paper { display: flex; align-items: center; gap: 8px; }
.compose-label { font-size: 13px; color: #333; white-space: nowrap; font-weight: 500; }
.compose-tip { font-size: 12px; color: #aaa; }
.compose-stat { font-size: 13px; color: #666; white-space: nowrap; }
.compose-tip-bar {
  background: #f0f5ff;
  border: 1px solid #adc6ff;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 12px;
  color: #1d39c4;
  margin-bottom: 10px;
}
.compose-list {
  max-height: 380px;
  overflow-y: auto;
  border: 1px solid #eee;
  border-radius: 4px;
}
.compose-q-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border-bottom: 1px solid #f5f5f5;
  cursor: pointer;
  transition: background 0.15s;
}
.compose-q-row:last-child { border-bottom: none; }
.compose-q-row:hover { background: #f8f9ff; }
.compose-q-row.compose-selected { background: #e6f4ff; }
.compose-q-body { flex: 1; min-width: 0; }
.compose-q-text {
  font-size: 13px;
  color: #333;
  line-height: 1.5;
  margin-bottom: 4px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
}
.compose-q-tags { display: flex; align-items: center; gap: 4px; }
.compose-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #f0f0f0;
}

/* ===== 通用 ===== */
.danger-text { color: #ff4d4f; }
.form-tip { font-size: 12px; color: #999; margin-top: 4px; }
.option-row { 
  display: flex; 
  align-items: flex-start; 
  margin-bottom: 12px; 
  gap: 8px; 
}
.option-key {
  width: 24px; 
  height: 24px;
  background: #1677ff; 
  color: #fff;
  border-radius: 50%;
  display: flex; 
  align-items: center; 
  justify-content: center;
  font-size: 12px; 
  font-weight: bold; 
  flex-shrink: 0;
  margin-top: 4px;
}
.option-editor {
  flex: 1;
}
.option-editor ::v-deep .rich-editor {
  border: 1px solid #dcdfe6;
}
.option-editor ::v-deep .w-e-text-container {
  min-height: 80px;
}
.filter-card { margin-bottom: 0; }

/* ===== 卡片标签展示行 ===== */
.q-tags-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px;
  margin-bottom: 8px;
  min-height: 24px;
}
.q-tags-icon {
  color: #999;
  font-size: 12px;
  margin-right: 4px;
  flex-shrink: 0;
}
.q-tags-sep {
  color: #ddd;
  margin: 0 6px;
  font-style: normal;
}
.q-tag-item {
  margin: 0 2px 0 0;
}
.q-tag-btn {
  margin-left: 4px;
  color: #aaa;
  font-size: 12px;
  padding: 0 4px;
}
.q-tag-btn:hover { color: #1677ff; }

/* ===== 快捷打标签 Dialog ===== */
.tag-quick-wrap { padding: 4px 0; }
.tag-quick-section { margin-bottom: 4px; }
.tag-quick-label {
  font-size: 13px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}
.tag-quick-selectors {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
}
.tag-quick-selected {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  min-height: 28px;
  padding: 4px 8px;
  background: #fafafa;
  border: 1px solid #eee;
  border-radius: 4px;
  align-items: center;
}
.tag-quick-empty {
  font-size: 12px;
  color: #ccc;
}
.tag-selector-wrap {
  width: 100%;
}
.tag-cascade-selectors {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.tag-selected-list {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  padding: 6px 8px;
  background: #fafafa;
  border: 1px solid #eee;
  border-radius: 4px;
  min-height: 36px;
}

/* ===== 批量操作悬浮栏 ===== */
.batch-bar {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  background: #1677ff;
  color: #fff;
  border-radius: 8px;
  padding: 10px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 4px 16px rgba(22,119,255,0.35);
  z-index: 1000;
  min-width: 320px;
}
.batch-info { font-size: 14px; flex: 1; }
.batch-info strong { font-size: 16px; }

/* 卡片选中状态 */
.q-card-selected {
  border-color: #91caff !important;
  background: #f0f7ff !important;
}
.q-card-added {
  opacity: 0.72;
  background: #fafafa !important;
}
.page-wrap.picker-mode {
  height: 100%;
  min-height: 0;
}
.page-wrap.picker-mode .main-content {
  min-height: 0;
}
.picker-batch-bar {
  min-width: 520px;
}

/* ===== 滑入动画 ===== */
.slide-up-enter-active, .slide-up-leave-active { transition: all 0.25s ease; }
.slide-up-enter, .slide-up-leave-to { opacity: 0; transform: translateX(-50%) translateY(20px); }

/* ===== 侧边栏学科/学段/年级选择 ===== */
.sidebar-section {
  border-bottom: 1px solid #f0f0f0;
  padding: 12px;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.sidebar-section:last-child {
  border-bottom: none;
}
.sidebar-title {
  font-size: 13px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.sidebar-tip {
  padding: 20px 12px;
  text-align: center;
  color: #999;
  font-size: 13px;
}
.sidebar-tip i {
  margin-right: 4px;
}

/* 学科列表 */
.subject-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.subject-item {
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 13px;
  color: #555;
  cursor: pointer;
  transition: all 0.15s;
}
.subject-item:hover {
  background: #f5f5f5;
  color: #1677ff;
}
.subject-item.active {
  background: #e6f4ff;
  color: #1677ff;
  font-weight: 500;
}

/* 学段列表 */
.stage-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.stage-item {
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 13px;
  color: #555;
  cursor: pointer;
  transition: all 0.15s;
}
.stage-item:hover {
  background: #f5f5f5;
  color: #1677ff;
}
.stage-item.active {
  background: #e6f4ff;
  color: #1677ff;
  font-weight: 500;
}

/* 年级列表 */
.grade-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.grade-item {
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 13px;
  color: #555;
  cursor: pointer;
  transition: all 0.15s;
}
.grade-item:hover {
  background: #f5f5f5;
  color: #1677ff;
}
.grade-item.active {
  background: #e6f4ff;
  color: #1677ff;
  font-weight: 500;
}

/* ===== 弹窗标题 & 表单 Label 字号加大 ===== */
::v-deep .el-dialog__title {
  font-size: 18px;
  font-weight: 600;
}
::v-deep .el-form-item__label {
  font-size: 15px;
}

.sub-question-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
}
.sub-total-score {
  color: #1677ff;
  font-weight: 600;
  font-size: 14px;
}
.fill-sub-banner {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin: 0 0 12px 100px;
  padding: 10px 14px;
  background: #e6f4ff;
  border: 1px solid #91caff;
  border-radius: 8px;
  font-size: 13px;
  color: #0958d9;
}
.sub-question-card {
  margin: 0 0 16px 100px;
  padding: 12px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fafafa;
}
.sub-question-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.sub-label {
  font-weight: 600;
  color: #1677ff;
  min-width: 36px;
}
.sub-score-label {
  color: #6b7280;
  font-size: 13px;
}
</style>
